// The Wilds: a graph of screens whose terrain is sampled from world-space
// noise (so lakes and woods run across screen seams), plus the tier gating
// that makes progression provable.
import { RNG, mix } from './rng.ts';
import { T, BIOME_NAMES, isSolid } from './art.ts';
import { buildTerrain, buildRegions, coastField } from './terrain.ts';
import { reachableTile, walkableTile, isWet, BARRIERS, OPEN } from './passable.ts';

export const COLS = 16, ROWS = 12;
const NEIGHBOURS = [[1, 0], [-1, 0], [0, 1], [0, -1]];
export const WORLD_W = 16, WORLD_H = 13;   // an archipelago wants room (LS-383; was 10 by 10)
/* THE WORLD IS OPEN (LS-225, Vijay: "we no longer need (deliberate) exploration gates either"). No barrier stands
 * anywhere; the tiers still shape the terrain and do not gate it. The gate machinery -- barriers on links, charm tiles
 * in doorways, vestibules and twins -- was folded out in LS-511 (2026-09-29), the worlds unchanged to the tile. */
/* NO HOLLOWS, NO CAVES (LS-225 step 5, deleted in LS-226). Vijay: "lets get
 * rid of the hallows and caves. instead the objective will be to defeat 3 very
 * large and powerful monsters that roam the overworld". The placement code is
 * gone; it had no hand in the terrain, so the world is the world it was, and
 * the three beasts are the whole quest. */


/* WHY THERE ARE THREE BOUNDARIES AND NOT TWO.
 *
 * There were two, so the THIRD charm — a third of the game's headline
 * collectibles, the reward for clearing the last hollow — opened nothing.
 * Measured by flooding `links`/`gates` from spawn over 100 worlds: 19.4 of 64
 * screens bare-handed, 40.0 with one charm, and **64.0 of 64 with two, in
 * 100 of 100 worlds**. Walking `reachableTile` over all 12,288 tiles for what
 * the last charm opened that the first two had not: 0 sealed caves in 39
 * worlds, and nothing whatsoever — no cave, no extra tile — in 8.
 *
 * The third boundary sits at the 85th percentile of BFS distance, so the last
 * charm opens the outer sixth of the map. (The hollows this once protected are
 * gone, LS-226; the tiers and the charm order stay for their own follow-up.) */

export const CHARMS = ['pods', 'tide', 'ember'];
/** The order this seed hands out its three charms. */
export function charmOrder(seedNum) {
  const rng = new RNG(mix(seedNum, 4242));
  const order = CHARMS.slice();
  rng.shuffle(order);
  // THE TIDE USED TO BE BARRED FROM LAST PLACE, AND IS NOT ANY MORE.
  //
  // The reasoning was sound when it was written: deep water is ambient, about
  // a tenth of every world's tiles rather than only its gates, so a world
  // that withholds the tide until the end could put a lake across the
  // mandatory route with no way round. That cost two of the six orders — a
  // third of this game's run-to-run variety — on the strength of an argument
  // rather than a measurement.
  //
  // Measured, with the generator as it now stands: `node test/gate.mjs 3000`
  // passes with tide last, and the only two seeds in 3,000 that ever failed
  // failed for an unrelated reason (a demonstration hedge painted across a
  // flooded room after its flood repair had run — fixed in `hollows.js`, and
  // it failed with the tide bar in place too). `ensureConnected` proves every
  // screen crossable with no charms at all before it proves anything else,
  // which is the guarantee this bar was standing in for.
  //
  // All six orders now occur, within 15.6%-17.9% of each other over 4,000
  // seeds. If a lake ever does close a route, `gate.mjs` is the thing that
  // says so, and it is run at 300 by default and higher on demand.
  return order;
}
/** The barrier a charm opens, as a tile. */
export const CHARM_TILE = { pods: T.RUBBLE, tide: T.WATER, ember: T.THORN };
/** What each barrier looks like on the world map and in its legend. The map
 *  used to hard-code red-then-blue for tier 1 and tier 2, which was true of
 *  the one order the game shipped with and wrong in 58% of seeds once the
 *  order became a permutation. */
export const CHARM_MARK = {
  pods: { col: '#c9553f', label: 'cracked' },
  tide: { col: '#3d7f96', label: 'water' },
  ember: { col: '#6f9a4a', label: 'thorn' },
};

const key = (x, y) => x + ',' + y;
const DIRS = [[0, -1], [1, 0], [0, 1], [-1, 0]];

function edgeKey(a, b) {
  return a.x < b.x || a.y < b.y ? `${a.x},${a.y}|${b.x},${b.y}` : `${b.x},${b.y}|${a.x},${a.y}`;
}

// --------------------------------------------------------------- noise
function lattice(seed, ix, iy) {
  return (mix(seed, ix + 1013, iy + 2027) >>> 8) / 16777216;
}
function smooth(t) { return t * t * (3 - 2 * t); }
function vnoise(seed, x, y) {
  const ix = Math.floor(x), iy = Math.floor(y);
  const fx = smooth(x - ix), fy = smooth(y - iy);
  const a = lattice(seed, ix, iy), b = lattice(seed, ix + 1, iy);
  const c = lattice(seed, ix, iy + 1), d = lattice(seed, ix + 1, iy + 1);
  return (a * (1 - fx) + b * fx) * (1 - fy) + (c * (1 - fx) + d * fx) * fy;
}
export function fbm(seed, x, y, scale) {
  return vnoise(seed, x * scale, y * scale) * 0.65 +
    vnoise(seed + 7919, x * scale * 2.3, y * scale * 2.3) * 0.35;
}

// --------------------------------------------------------------- world
export function generateWorld(seedNum) {
  const rng = new RNG(mix(seedNum, 1));

  /* THE WORLD GETS ITS GROUND BEFORE IT GETS ITS ROOMS.
   *
   * `buildTerrain` was imported by `src/game.ts` and by nothing else, so the
   * heightmap fed the cliff draw and the lighting normals and never reached
   * the generator. Every structural decision below — where a wall stands,
   * which biome a place is, how dense the woods are — was therefore keyed to
   * the SCREEN, with world-space noise applied afterwards as texture. That is
   * why the fields look continuous and the structure reads as a grid: the
   * chunk boundary was a decision boundary.
   *
   * The terrain is a property of the world, so it is built here, once, and
   * every screen is a window onto it. */
  const terrain = buildTerrain(seedNum, WORLD_W * COLS, WORLD_H * ROWS);

  // screen graph: randomized DFS spanning tree plus a few extra loops
  /** Is there rock here? Asked of `terrain.face`, which is the one place that
   *  says so — and which knows about the tiles a TALL wall claims as well as
   *  the terrace feet. Working it out from the terraces again finds only the
   *  feet, so a hollow's door was sited in the lower courses of a cliff and
   *  cut straight through it: seed-297, a mouth at 70,28 with rock on three
   *  sides, which is also how that seed leaked a tier. */
  const isStep = (tx, ty) => terrain.face[terrain.at(tx, ty)] > 0;

  /* TIER, READ OFF THE LAND INSTEAD OF IMPOSED ON IT.
   *
   * `tierOf` is BFS distance over the SCREEN-PAIR graph, and that graph is an
   * artefact: derive its links from the terrain and 106 of 112 adjacent pairs
   * are joined, because one walkable tile anywhere along a seam joins them. It
   * only carries a barrier because every screen is walled, and the wall is the
   * lattice. Regions do carry one — measured across seeds, the plateaus form a
   * TREE (pairs always equals regions minus one), so every ramp between two of
   * them is a bridge and closing it genuinely cuts the world.
   *
   * Depth from the region the player wakes in, bucketed, is the tier. Nothing
   * reads this yet; it is built here so the structural passes can move onto it
   * without the screen graph having to be right in the meantime. */
  const { id: regionId, regions: regionList, pairs: regionPairs, isSea, coast } = buildRegions(terrain);
  const radj = new Map();
  for (const r of regionList) radj.set(r.r, []);
  for (const p of regionPairs) {
    if (!radj.has(p.a) || !radj.has(p.b)) continue;
    radj.get(p.a).push(p); radj.get(p.b).push(p);
  }
  /* THE PLAYER WAKES ON THE SHORE, and that is not a flavour choice.
   *
   * Depth is measured from wherever they start, so starting on the middle
   * shelf makes the shore and the peak both one step away and there are three
   * tiers instead of four. Starting on the lowest terrace makes the rings
   * nest, which is the whole reason to read tier off the land. Measured: max
   * depth 2 from the largest region, 3 from the lowest one, every seed. */
  /* AMONG THE LAND (LS-383): the sea floor is one flat region at the lowest level now, and it was being picked as
   * the start region, which put the glade in the sea on six worlds of eight. */
  const landRegions = regionList.filter((r: any) => terrain.fill[terrain.at(r.cx, r.cy)] >= terrain.sea);
  /* ...AND AMONG THE JOINED (LS-383, the merge). A lowest land region with no neighbour it can be ramped to gave
   * the glade a depth map of one entry: no ramps, no tiers, and the cliffs walled the start pocket in (world-7
   * reached 6 screens of 208). A start region must have somewhere to climb to. */
  const joined = landRegions.filter((r: any) => radj.has(r.r) && radj.get(r.r).length > 0);
  const pool0 = joined.length ? joined : landRegions.length ? landRegions : regionList;
  const lowest = Math.min(...pool0.map((r: any) => r.lvl));
  const startRegion = pool0
    .filter((r: any) => r.lvl === lowest)
    .sort((a: any, b: any) => b.n - a.n)[0].r;
  const depth = new Map();
  /* EVERY ISLAND CLIMBS FROM ITS OWN SHORE (LS-383, the merge). Depth spread from the start region only, and a
   * region pair never crosses the sea, so no region on any other island had a depth, no ramp was ever cut there,
   * and every terrace across the water was a wall: on reach-0 all three lairs stood on islands nobody could climb.
   * Each connected piece of the pair graph now starts at its own lowest, largest joined region. */
  const spread = (root) => {
    if (!radj.has(root) || depth.has(root)) return;
    depth.set(root, 0);
    const q = [root];
    for (let i = 0; i < q.length; i++) {
      const c = q[i];
      for (const p of radj.get(c)) {
        const n = p.a === c ? p.b : p.a;
        if (depth.has(n) || !radj.has(n)) continue;
        depth.set(n, depth.get(c) + 1);
        q.push(n);
      }
    }
  };
  spread(startRegion);
  for (const r of [...joined].sort((a: any, b: any) => a.lvl - b.lvl || b.n - a.n)) spread(r.r);
  /* AND A REGION WITH NO PAIR IS ITS OWN ROOT (2026-09-17). `spread` walks the pair graph, so a land region that is
   * paired with nothing -- an island that is one plateau, with no cliff foot to climb -- was never given a depth at
   * all, and therefore no tier. The seal reads a missing tier as out of reach at every charm, so whole islands were
   * shut: nine worlds in twenty-four had one, the largest 1,753 tiles, and two of the three lairs stood on them in
   * the worst. Depth 0, hence tier 0, which is what unclimbed ground on its own shore should cost. Every depth the
   * two passes above assign is untouched, so the fifteen worlds with no unpaired region generate bit-identically. */
  {
    /* EVERY REGION THAT HOLDS DRY LAND, not only the ones whose centroid does. `landRegions` tests the single tile
     * at a region's centre, and a region wrapped round a bay has its centre in the water: one such region of 783
     * tiles was still untiered after the first pass. Asked of the tiles themselves, there is nowhere left. */
    const hasLand = new Set<number>();
    for (let j = 0; j < regionId.length; j++) { const r = regionId[j]; if (r >= 0 && !hasLand.has(r) && terrain.fill[j] >= terrain.sea) hasLand.add(r); }
    for (const r of hasLand) if (!depth.has(r)) depth.set(r, 0);
  }
  /** Which charm you must hold to stand here: 0 none, 1..3 by the world's order.
   *  One ring per tier, so a step inward is always exactly one charm. */
  const regionTier = new Map();
  for (const [r, d] of depth) regionTier.set(r, Math.min(3, d));
  /* A ramp per join, and only one, because a cliff you can climb anywhere is
   * not a cliff. The barrier belongs to the tier of the DEEPER side, so
   * walking outward is always free and walking inward costs the charm. */
  const rampRng = new RNG(mix(seedNum, 7717));
  const rampUsed = new Set();
  const ramps: any[] = [];
  /* ONE RAMP PER PAIR -- MEASURED, NOT ASSUMED.
   *
   * A pair whose feet run in two separate stretches was thought to need a ramp
   * per stretch: wide-0's 465-tile shelf sat behind the second stretch of the
   * 2-5 pair with the pair's one ramp on the first. Built, and measured over
   * thirty worlds at stretch separations of 1, 4, 6, 10, 16 and infinity: the
   * residual was IDENTICAL at every one (two worlds, 129 tiles), and the ramp
   * count ran from 36 a world down to 14.7. What had fixed wide-0 was the
   * ford the seal lays through sea, not the extra ramps. A rule that changes
   * nothing but the number of cliffs you can climb is not a topology rule. */
  const STRETCH_GAP = (globalThis as any).__stretchGap ?? 999;
  const stretches = (tiles) => {
    const left = tiles.slice();
    const out = [];
    while (left.length) {
      const run = [left.shift()];
      let grew = true;
      while (grew) {
        grew = false;
        for (let i = 0; i < left.length; i++) {
          const c = left[i];
          let near = false;
          for (const r of run) {
            if (Math.abs(r[0] - c[0]) <= STRETCH_GAP && Math.abs(r[1] - c[1]) <= STRETCH_GAP) { near = true; break; }
          }
          if (near) { run.push(c); left.splice(i, 1); i--; grew = true; }
        }
      }
      out.push(run);
    }
    return out;
  };
  /* A RAMP EVERY TWENTY TILES OF WALL, NOT ONE A PAIR. "I can't find any
   * ramps to go up." Measured from the spawn, walking: the nearest ramp was
   * 23 tiles away on depthlook, 40 on contrast-0, 65 on seed-92 and 117 on
   * wide-0 -- a whole lowland with no way up, because one ramp served a
   * pair of regions however long their shared wall. The pair's foot tiles
   * are sorted along the wall and cut into stretches of about twenty, and
   * each stretch gets its own ramp by the same scoring. */
  const RAMP_EVERY = 20;
  const rampPairs = [];
  for (const p of regionPairs) {
    const tiles = p.tiles.slice().sort((u, v) => (u[0] + u[1] * 0.37) - (v[0] + v[1] * 0.37));
    const n = Math.max(1, Math.round(tiles.length / RAMP_EVERY));
    for (let i = 0; i < n; i++) {
      const slice = tiles.slice(Math.floor(i * tiles.length / n), Math.floor((i + 1) * tiles.length / n));
      if (slice.length) rampPairs.push({ a: p.a, b: p.b, tiles: slice });
    }
  }
  for (const p of rampPairs) {
    if (!depth.has(p.a) || !depth.has(p.b)) continue;
    const deep = depth.get(p.a) > depth.get(p.b) ? p.a : p.b;
    /* THE RAMP GOES WHERE THERE IS ROOM FOR IT.
     *
     * A candidate is a single cliff-foot tile with a different plateau on
     * either side of it, so cutting that one tile joins them. Prefer one with
     * open land around it — a foot on a one-tile spit joins two plateaus
     * through a gap nothing can actually walk to. */
    const room = (tx, ty) => {
      let n = 0;
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          const nx = tx + dx, ny = ty + dy;
          if (nx < 0 || ny < 0 || nx >= terrain.TW || ny >= terrain.TH) continue;
          if (terrain.fill[terrain.at(nx, ny)] >= terrain.sea) n++;
        }
      }
      return n;
    };
    /* ...AND NO TWO RAMPS STAND ON THE SAME TILE.
     *
     * A foot with three plateaus around it is a candidate for each PAIR of
     * them, so two ramps could be sited on one tile — and the second one
     * written won. Measured: 18 of 1,191 ramps carried a barrier weaker than
     * their own tier, which is a tier opened by the wrong charm. On seed-1 a
     * tier-3 ramp was deep water and the tide is that world's SECOND charm.
     *
     * A used tile is heavily penalised rather than forbidden, because a pair
     * may have only the one candidate; where sharing is unavoidable the
     * stronger gate is kept below, so the worst case is a crossing that costs
     * more than it should rather than one that costs less. */
    /* ...AND NEVER ONTO ANOTHER WALL.
     *
     * A candidate is a foot tile; nothing here looked at what it opens ONTO. A
     * foot on a two-thick wall has rock on its low side, so the ramp cut there
     * is a doorway into a cliff. Traced on contrast-0: the ramp at 48,12 exits
     * east onto CLIFF and the 224-tile region behind it is unreachable holding
     * every charm. The low side is the tile opposite the higher neighbour --
     * the same tile `downTile` clears below -- and a candidate whose low side is
     * rock loses by more than any amount of room can buy back. */
    const lowIsRock = (cx, cy) => {
      const lv0 = terrain.terrace[terrain.at(cx, cy)];
      for (const [dx, dy] of NEIGHBOURS) {
        const nx = cx + dx, ny = cy + dy;
        if (nx < 0 || ny < 0 || nx >= terrain.TW || ny >= terrain.TH) continue;
        if (terrain.terrace[terrain.at(nx, ny)] > lv0) {
          const lx = cx - dx, ly = cy - dy;
          if (lx < 0 || ly < 0 || lx >= terrain.TW || ly >= terrain.TH) return true;
          return !!terrain.face[terrain.at(lx, ly)] || terrain.fill[terrain.at(lx, ly)] < terrain.sea;
        }
      }
      return false;
    };
    /* A CORRIDOR MUST NOT HAVE A THIRD REGION BESIDE IT. seed-16: a run cut
     * three tiles through a thin band had tier-0 ground on one side of its
     * middle tile and tier-1 ground on the other, and was laid free -- its
     * two ends were both tier 1 -- so the cut itself was a door: "tier 1
     * walked BARE-HANDED". Every open tile beside a run must belong to the
     * pair the run joins; a run with any other neighbour is refused. */
    const sidesClean = (cand) => {
      const dx = Math.sign(cand[2] - cand[0]), dy = Math.sign(cand[3] - cand[1]);
      const n = Math.max(Math.abs(cand[2] - cand[0]), Math.abs(cand[3] - cand[1]));
      for (let i = 0; i <= n; i++) {
        const rx = cand[0] + dx * i, ry = cand[1] + dy * i;
        for (const [ex, ey] of NEIGHBOURS) {
          if (n > 0 && ((ex === dx && ey === dy) || (ex === -dx && ey === -dy))) continue;  // along the run
          const nx = rx + ex, ny = ry + ey;
          if (nx < 0 || ny < 0 || nx >= terrain.TW || ny >= terrain.TH) continue;
          const rr = regionId[terrain.at(nx, ny)];
          if (rr >= 0 && rr !== p.a && rr !== p.b) return false;
        }
      }
      return true;
    };
    let t = p.tiles[0], best = -1;
    for (const cand of p.tiles) {
      const score = room(cand[0], cand[1])
        - (rampUsed.has(cand[0] + ',' + cand[1]) ? 1000 : 0)
        - (lowIsRock(cand[0], cand[1]) ? 500 : 0)
        - (sidesClean(cand) ? 0 : 2000)
        + (rampRng.int(3) === 0 ? 1 : 0);
      if (score > best) { best = score; t = cand; }
    }
    rampUsed.add(t[0] + ',' + t[1]);
    /* THE BARRIER ANSWERS FOR EVERY PLATEAU THE TILE TOUCHES.
     *
     * A foot can have three regions around it, and cutting it joins all of
     * them — but the tier came from the one PAIR this ramp was created for. So
     * a ramp built to join two tier-1 plateaus, and barred with the tier-1
     * charm, also opened a tier-2 plateau that happened to touch the same
     * tile. Traced on seed-9: land at 11,91 drawn as deep water, with tier-1
     * ground on one side and tier-2 ground on the other, in a world whose
     * first charm is the tide.
     *
     * The gate is the deepest thing it opens. That can only ever make a
     * crossing cost more than the pair alone would ask, never less. */
    /* THE FOOTPRINT FIRST, THEN THE GATE OVER ALL OF IT.
     *
     * A ramp is two tiles wide, and the second tile has its own neighbours —
     * so a gate computed from the first tile alone can be weaker than what the
     * pair actually opens. Measured the moment ramps were widened: two seeds
     * in two hundred let a player into a deeper tier holding one charm. Decide
     * the footprint, then ask every plateau either tile touches. */
    const pxw = t[3] !== t[1] ? 1 : 0, pyw = t[3] !== t[1] ? 0 : 1;
    const wx2 = t[0] + pxw, wy2 = t[1] + pyw;
    const wide = (wx2 < terrain.TW && wy2 < terrain.TH
      && terrain.face[terrain.at(wx2, wy2)]) ? [wx2, wy2] : null;
    let tier = regionTier.get(deep) || 0;
    let lowest = tier;
    /* EVERY TILE OF THE RUN, AND EVERY REGION BESIDE IT. seed-16's leak was a
     * run laid free between two tier-1 regions with tier-0 ground against
     * its middle tile; refusing such runs outright cost seed-37 its third
     * hollow. So the run is gated by the highest tier of ANY region touching
     * any of its tiles, and is free only when every one of them is the same
     * tier: the gate stands wherever the cut could be walked into. */
    const runTiles = [];
    {
      const rdx = Math.sign(t[2] - t[0]), rdy = Math.sign(t[3] - t[1]);
      const rn = Math.max(Math.abs(t[2] - t[0]), Math.abs(t[3] - t[1]));
      for (let i = 0; i <= rn; i++) runTiles.push([t[0] + rdx * i, t[1] + rdy * i]);
      if (wide) runTiles.push(wide);
    }
    for (const [bx, by] of runTiles) {
      for (const [dx, dy] of NEIGHBOURS) {
        const nx = bx + dx, ny = by + dy;
        if (nx < 0 || ny < 0 || nx >= terrain.TW || ny >= terrain.TH) continue;
        const rr = regionId[terrain.at(nx, ny)];
        if (rr >= 0 && regionTier.has(rr)) {
          tier = Math.max(tier, regionTier.get(rr));
          lowest = Math.min(lowest, regionTier.get(rr));
        }
      }
    }
    /* A CLIFF YOU CAN SIMPLY WALK UP, where walking up costs nothing.
     *
     * Every crossing between plateaus changes tier by one — that is what BFS
     * depth means — so every one of them was charm-gated and the world had no
     * cliff a player could climb by walking. Where a foot touches only
     * plateaus of the SAME tier, though, crossing it grants nothing that tier
     * does not already have, so the gate is pointless and the step is free.
     *
     * `lowest === tier` is the test, and it has to be taken over EVERY region
     * around the tile rather than over the pair, for the same reason the gate
     * is: a foot can have three plateaus around it and opening it opens all of
     * them. Two or three per world, cut as visible steps. */
    /* AND ONE TILE DOWN THE LOW SIDE, so the flight has somewhere to climb.
     *
     * Widening the ramp fixed it being too narrow to draw; it was still one
     * tile TALL, and sixteen pixels cannot carry a flight of steps any better
     * vertically than horizontally. The extra tile is ordinary ground on the
     * low side — walkable before and after, so nothing about the gating moves
     * — and it gives the slope thirty-two pixels to read in. */
    let downTile = null;
    {
      let hx = 0, hy = 0;
      const lv0 = terrain.terrace[terrain.at(t[0], t[1])];
      for (const [dx, dy] of NEIGHBOURS) {
        const nx = t[0] + dx, ny = t[1] + dy;
        if (nx < 0 || ny < 0 || nx >= terrain.TW || ny >= terrain.TH) continue;
        if (terrain.terrace[terrain.at(nx, ny)] > lv0) { hx = dx; hy = dy; }
      }
      const dx2 = t[0] - hx, dy2 = t[1] - hy;
      if ((hx || hy) && dx2 >= 0 && dy2 >= 0 && dx2 < terrain.TW && dy2 < terrain.TH
        && terrain.fill[terrain.at(dx2, dy2)] >= terrain.sea) {
        const jl = terrain.at(dx2, dy2);
        /* THE LOW SIDE IS OPENED EVEN WHEN IT IS ROCK -- if that rock is only
         * the wall's own claimed body and not a foot of its own. A ramp on a
         * two-thick wall otherwise exits into stone: five of 44 ramps across
         * four worlds had no open tile on their low side, and the penalty on
         * siting cannot help a pair whose every candidate stands on such a
         * wall. Claimed rock under a ramp has no face drawn over it (the ramp
         * is the cut), so un-claiming it changes nothing anyone can see. */
        if (terrain.face[jl]) {
          const lvl = terrain.terrace[jl];
          let isFoot = false;
          for (const [ddx, ddy] of NEIGHBOURS) {
            const nx = dx2 + ddx, ny = dy2 + ddy;
            if (nx < 0 || ny < 0 || nx >= terrain.TW || ny >= terrain.TH) continue;
            if (terrain.terrace[terrain.at(nx, ny)] > lvl) { isFoot = true; break; }
          }
          // ...and only where the opened tile meets open ground: with a ramp
          // every twenty tiles of wall, one un-claim landed on a tile buried
          // in a stack and left a single open tile sealed in rock (wide-0)
          let meetsOpen = false;
          for (const [ddx, ddy] of NEIGHBOURS) {
            const nx = dx2 + ddx, ny = dy2 + ddy;
            if (nx < 0 || ny < 0 || nx >= terrain.TW || ny >= terrain.TH) continue;
            if (nx === t[0] && ny === t[1]) continue;
            const jn = terrain.at(nx, ny);
            if (!terrain.face[jn] && terrain.fill[jn] >= terrain.sea) { meetsOpen = true; break; }
          }
          if (!isFoot && meetsOpen) { terrain.face[jl] = 0; if (terrain.crag) terrain.crag[jl] = 0; }
        }
        if (!terrain.face[jl]) downTile = [dx2, dy2];
      }
    }
    const free = lowest === tier;
    ramps.push({ down: downTile, x: t[0], y: t[1], x2: t[2], y2: t[3], a: p.a, b: p.b,
      tier: free ? 0 : tier, free, wide });
  }


  /* WHICH ISLAND A SCREEN IS ON (LS-383). Land tiles flood into components; a screen takes the component most of
   * its land belongs to, or -1 when it is all sea. The lairs read it to sit on different islands, and the checks. */
  const island = new Int16Array(WORLD_W * WORLD_H).fill(-1);
  let islandCount = 0;
  { const TW = WORLD_W * COLS, TH = WORLD_H * ROWS, comp = new Int32Array(TW * TH).fill(-1);
    const isLand = (j) => terrain.fill[j] >= terrain.sea;
    for (let y0 = 0; y0 < TH; y0++) for (let x0 = 0; x0 < TW; x0++) {
      const j0 = terrain.at(x0, y0); if (comp[j0] >= 0 || !isLand(j0)) continue;
      const id = islandCount++; const q = [[x0, y0]]; comp[j0] = id;
      while (q.length) { const [x, y] = q.pop(); for (const [dx, dy] of DIRS) { const nx = x + dx, ny = y + dy; if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue; const j = terrain.at(nx, ny); if (comp[j] >= 0 || !isLand(j)) continue; comp[j] = id; q.push([nx, ny]); } }
    }
    for (let sy = 0; sy < WORLD_H; sy++) for (let sx = 0; sx < WORLD_W; sx++) {
      const votes = new Map();
      for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) { const c = comp[terrain.at(sx * COLS + x, sy * ROWS + y)]; if (c >= 0) votes.set(c, (votes.get(c) || 0) + 1); }
      let best = -1, n = 0; for (const [c, k] of votes) if (k > n) { n = k; best = c; }
      island[sy * WORLD_W + sx] = n >= 8 ? best : -1;   // a screen with fewer than eight land tiles is sea with a rock in it
    }
    // renumber by size so the home island is 0 and islets count last
    const sizes = new Map(); for (const v of island) if (v >= 0) sizes.set(v, (sizes.get(v) || 0) + 1);
    const order = [...sizes.keys()].sort((a, b) => sizes.get(b) - sizes.get(a)); const rank = new Map(order.map((c, i) => [c, i]));
    for (let i = 0; i < island.length; i++) if (island[i] >= 0) island[i] = rank.get(island[i]);
    islandCount = order.length;
  }
  const links = new Set();
  const visited = new Set();
  /* THE SPAWN SCREEN FOLLOWS THE START REGION.
   *
   * This was any screen on the bottom row, chosen before the land was
   * consulted — so the player could wake on the middle shelf or in the sea,
   * and every tier below them would be unreachable in the wrong direction.
   * Tier is depth from the region they wake in, so where they wake is the
   * origin of the whole progression and has to be ON it. */
  const [spx0, spy0] = spawnTile(seedNum);
  const startCandidates: any[] = [];
  for (let y = WORLD_H - 1; y >= 0 && !startCandidates.length; y--) {
    for (let x = 0; x < WORLD_W; x++) {
      /* AND THE TILE THEY ACTUALLY WAKE ON HAS TO BE STANDABLE.
       *
       * The glade that clears the spawn refuses to cut a cliff now — it is
       * locked like every other terrace foot — so a spawn screen whose spawn
       * tile happens to BE a foot wakes the player inside the rock. Traced on
       * seed t1: the flood started on a CLIFF at 104,90 and stepped straight
       * up into a tier-1 region, which is also why that seed read as a gating
       * leak. Choose a screen whose spawn tile is dry, level ground. */
      const tx = x * COLS + spx0, ty = y * ROWS + spy0;
      const j = terrain.at(tx, ty);
      if (regionId[j] !== startRegion) continue;
      if (islandCount > 1 && island[y * WORLD_W + x] !== 0) continue;   // the glade is on the largest island (LS-383)
      if (terrain.fill[j] < terrain.sea || terrain.river[j] || isStep(tx, ty)) continue;
      // a glade is composed around the spawn tile, and the glade pass can pave
      // trees and boulders but not sea or a face: every tile of the ring must
      // be ground the terrain already gives (24 of 400 spawns had a clear ring)
      let ringClear = true;
      for (let ey = -1; ey <= 1 && ringClear; ey++) for (let ex = -1; ex <= 1 && ringClear; ex++) {
        if (!ex && !ey) continue;
        const nx = tx + ex, ny = ty + ey;
        if (nx < 0 || ny < 0 || nx >= terrain.TW || ny >= terrain.TH) { ringClear = false; break; }
        const jn = terrain.at(nx, ny);
        if (terrain.fill[jn] < terrain.sea || terrain.river[jn] || terrain.face[jn] || regionId[jn] !== startRegion) ringClear = false;
      }
      if (!ringClear) continue;
      startCandidates.push({ x, y });
    }
  }
  /* NEVER THE SEA (LS-383). With islands, a start region can hold no spawn tile with a clear ring; the fallback was a
   * random screen on the bottom row, which is open water now. Second choice: any screen of the largest island whose
   * spawn tile is dry; third, any dry spawn tile at all. The glade pass paves what it must. */
  if (!startCandidates.length) {
    const dry = (x, y) => { const j = terrain.at(x * COLS + spx0, y * ROWS + spy0); return terrain.fill[j] >= terrain.sea && !terrain.river[j] && !isStep(x * COLS + spx0, y * ROWS + spy0); };
    // a ring of dry, faceless ground round the spawn tile, whatever region it is in: the glade must open onto the island
    const ring = (x, y) => { const tx = x * COLS + spx0, ty = y * ROWS + spy0; for (let ey = -2; ey <= 2; ey++) for (let ex = -2; ex <= 2; ex++) { const nx = tx + ex, ny = ty + ey; if (nx < 0 || ny < 0 || nx >= terrain.TW || ny >= terrain.TH) return false; const jn = terrain.at(nx, ny); if (terrain.fill[jn] < terrain.sea || terrain.river[jn] || terrain.face[jn]) return false; } return true; };
    for (let y = 0; y < WORLD_H; y++) for (let x = 0; x < WORLD_W; x++) if ((islandCount <= 1 || island[y * WORLD_W + x] === 0) && dry(x, y) && ring(x, y)) startCandidates.push({ x, y });
    if (!startCandidates.length) for (let y = 0; y < WORLD_H; y++) for (let x = 0; x < WORLD_W; x++) if ((islandCount <= 1 || island[y * WORLD_W + x] === 0) && dry(x, y)) startCandidates.push({ x, y });
    if (!startCandidates.length) for (let y = 0; y < WORLD_H; y++) for (let x = 0; x < WORLD_W; x++) if (dry(x, y)) startCandidates.push({ x, y });
  }
  const start = startCandidates.length
    ? startCandidates[rng.int(startCandidates.length)]
    : { x: rng.int(WORLD_W), y: WORLD_H - 1 };
  const stack = [start];
  visited.add(key(start.x, start.y));
  while (stack.length) {
    const cur = stack[stack.length - 1];
    const opts = rng.shuffle(DIRS.slice()).map(([dx, dy]) => ({ x: cur.x + dx, y: cur.y + dy }))
      .filter(n => n.x >= 0 && n.y >= 0 && n.x < WORLD_W && n.y < WORLD_H && !visited.has(key(n.x, n.y)));
    if (!opts.length) { stack.pop(); continue; }
    links.add(edgeKey(cur, opts[0]));
    visited.add(key(opts[0].x, opts[0].y));
    stack.push(opts[0]);
  }
  for (let i = 0; i < WORLD_W * WORLD_H * 0.35; i++) {
    const x = rng.int(WORLD_W), y = rng.int(WORLD_H);
    const [dx, dy] = rng.pick(DIRS);
    const n = { x: x + dx, y: y + dy };
    if (n.x < 0 || n.y < 0 || n.x >= WORLD_W || n.y >= WORLD_H) continue;
    links.add(edgeKey({ x, y }, n));
  }

  // open the spawn: one exit makes the first minute a guessing corridor
  const startNeighbours = DIRS
    .map(([dx, dy]) => ({ x: start.x + dx, y: start.y + dy }))
    .filter(n => n.x >= 0 && n.y >= 0 && n.x < WORLD_W && n.y < WORLD_H);
  const openCount = () => startNeighbours.filter(n => links.has(edgeKey(start, n))).length;
  for (const n of rng.shuffle(startNeighbours.slice())) {
    if (openCount() >= 3) break;
    links.add(edgeKey(start, n));
  }

  const dist = bfs(start, links);

  // --- tiers by BFS distance. Because distance is monotone along shortest
  // paths, every screen in tier N is reachable using only tier<=N gates.
  const dists = [...dist.values()].sort((a, b) => a - b);
  let d1 = dists[Math.floor(dists.length / 3)];
  let d2 = dists[Math.floor(dists.length * 2 / 3)];
  // the outer sixth, which is what the third charm opens — see GATE above
  let d3 = dists[Math.floor(dists.length * 0.85)];
  const maxD = dists[dists.length - 1];
  if (d1 < 1) d1 = 1;
  if (d2 <= d1) d2 = d1 + 1;
  if (d2 > maxD) d2 = Math.max(d1 + 1, maxD);
  /* A tier that cannot exist is better than a tier with nothing in it: on a
   * cramped world every boundary can collapse onto the last one, and a third
   * gate the far side of which is a single screen is worse than no third gate.
   * `d3 > maxD` leaves tier 3 empty and the world plays as it did. */
  if (d3 <= d2) d3 = d2 + 1;
  const tierOf = (x, y) => {
    const d = dist.get(key(x, y));
    if (d === undefined) return 0;
    return d < d1 ? 0 : d < d2 ? 1 : d < d3 ? 2 : 3;
  };
  /* A SCREEN'S TIER IS THE LAND'S TIER, not a distance in the link graph.
   *
   * `tierOf` is BFS over the screen-pair graph, which stopped describing
   * anything the moment the border wall went: derive that graph from the
   * terrain and 106 of its 112 edges exist, because one walkable tile
   * anywhere along a seam joins two screens. Everything downstream that asks
   * "how deep is this place" — where the hollows go, which charm a barrier
   * wants — has to ask the thing that actually stops the player, which is the
   * cliff between two plateaus. */
  /* ...AND IT IS THE TIER OF THE LAND THE SCREEN ACTUALLY HOLDS.
   *
   * Sampling the middle tile gave every all-ocean screen a tier of 0 by
   * default, and a hollow was then placed on one and had its door cut into the
   * sea: traced on seed-8, Hollow I's mouth at 117,6 in no region at all, with
   * the player reaching 151 tiles of the world. The tier is the tier of
   * whichever region owns the most ground here, and `landTiles` says whether
   * there is enough of it to put anything on. */
  const tier = [];
  const landTiles = [];
  for (let y = 0; y < WORLD_H; y++) {
    const row: any[] = [], counts: any[] = [];
    for (let x = 0; x < WORLD_W; x++) {
      const tally = new Map();
      let land = 0;
      for (let ly = 0; ly < ROWS; ly++) {
        for (let lx = 0; lx < COLS; lx++) {
          const r = regionId[terrain.at(x * COLS + lx, y * ROWS + ly)];
          if (r < 0 || !regionTier.has(r)) continue;
          land++;
          tally.set(r, (tally.get(r) || 0) + 1);
        }
      }
      let bestR = -1, bestN = 0;
      for (const [r, n] of tally) if (n > bestN) { bestN = n; bestR = r; }
      row.push(bestR >= 0 ? regionTier.get(bestR) : 0);
      counts.push(land);
    }
    tier.push(row);
    landTiles.push(counts);
  }

  // the order this world hands out its charms, decided once and used by the
  // borders, the caves and the hollows alike
  const order = charmOrder(seedNum);

  /* BIOME IS A PROPERTY OF THE PLACE, not of the chunk it fell in.
   *
   * It was one name per SCREEN, flood-filled over the screen grid, so a
   * screen was entirely dust and its neighbour entirely grove with a razor
   * straight edge 256px long between them — visible from orbit on a rendered
   * world map, and the first thing anyone says about it.
   *
   * The five biomes a world draws are still chosen per world, so seeds still
   * differ. What changed is what decides WHERE each one lies: the terrace you
   * are standing on and how wet it is. Both are world-space fields, so the
   * boundaries are organic and cross seams — and, better, they coincide with
   * the cliffs, because a terrace step is exactly where the biome changes.
   * Climbing out of the fen into the scree is then one act, not two. */
  /* A COUNTRY IS A PLACE, NOT AN ALTITUDE.
   *
   * Biome was `bucketBiome[terrace * 2 + wet]`, which ties the country to the
   * height — so every terrace band was a different biome and a hillside
   * climbing three levels showed three of them in parallel stripes. Sampled
   * across one screen: bloom on terrace 0, fen on 1, ash on 2, changing on the
   * exact line the cliff stands on. That is not a world with hills in it, it
   * is a contour map coloured by elevation.
   *
   * Countries are regions of the MAP. Seeds are scattered in world tiles and
   * each tile takes the nearest, with the distance warped by a low-frequency
   * noise so the borders are ragged rather than the straight bisectors a plain
   * Voronoi gives. A country then spans whatever terraces happen to run
   * through it, which is what makes a hillside read as one place seen from top
   * to bottom — and the terrace still shades it, so height is still legible. */
  const biomeSeeds: any[] = [];
  let mildSeed = 'heath';   // the mild country the opening screen is promised (LS-376), planted twice: see below
  {
    const picked = rng.shuffle(BIOME_NAMES.slice()).slice(0, 5);
    const TW2 = WORLD_W * COLS, TH2 = WORLD_H * ROWS;
    for (const b of picked) {
      biomeSeeds.push({ b, x: rng.irange(6, TW2 - 7), y: rng.irange(6, TH2 - 7) });
    }
    /* THE RUN OPENS IN A MILD COUNTRY (LS-376, 2026-09-11). Measured over 24
     * worlds before this: two opened in ash, which scorches at noon, and
     * twelve in scree or fen, which chill at night -- a run beginning by
     * hurting a player who has no fire, no hide and no idea why. The country
     * is decided here, after the spawn, so the cheapest guarantee is a sixth
     * seed planted ON the spawn tile in a mild country: nearest wins, so the
     * opening screen is mild, and the rest of the map keeps the seeds it had.
     * No random draw is spent on it, so the maze and every later pass are as
     * they were. Mild is heath, bloom or dust -- dust reaches 0.78 at noon,
     * under the scorch line, and 0.54 at night, well over the frost line. */
    const mild = picked.find((b) => b === 'heath' || b === 'bloom' || b === 'dust') || 'heath';
    biomeSeeds.push({ b: mild, x: start.x * COLS + spx0, y: start.y * ROWS + spy0 });
    mildSeed = mild;   // ...and it is planted again below, at the WARPED point the opening screen's country is read from
  }
  /* THE WARP HAS TO BEAT THE BISECTOR.
   *
   * Between two Voronoi seeds the border is a straight line — that is what a
   * bisector is — and a warp gentler than the line is long merely bends it.
   * Measured: the longest near-straight run of a single border was 22, 25 and
   * 39 tiles across three seeds, which at sixteen pixels a tile is most of a
   * map edge and reads as a ruled line on a political map. An art review
   * called it out and blamed the chunk grid; the grid was innocent — 6.7% of
   * borders land on a screen edge against 6.3% by chance — and the bisector
   * was the culprit.
   *
   * Three octaves, the coarsest at nearly the scale of a country, so a border
   * wanders as far as the countries are wide; then one at hill scale and one
   * at tile scale to fray it. Both the country lookup and the blend read the
   * same warp, or the margins would be drawn against a different border from
   * the one the ground uses. */
  const warpX = (cx, cy) => (fbm(seedNum + 5501, cx, cy, 0.017) - 0.5) * 62
    + (fbm(seedNum + 5503, cx, cy, 0.075) - 0.5) * 18
    + (fbm(seedNum + 5505, cx, cy, 0.42) - 0.5) * 5;
  const warpY = (cx, cy) => (fbm(seedNum + 5507, cx, cy, 0.017) - 0.5) * 62
    + (fbm(seedNum + 5509, cx, cy, 0.075) - 0.5) * 18
    + (fbm(seedNum + 5511, cx, cy, 0.42) - 0.5) * 5;
  const biomeCache = new Map();
  const blendCache = new Map();
  /* AND THE COUNTRIES INTERLEAVE WHERE THEY MEET.
   *
   * A threshold on a smooth field is a clean contour, so `moist > 0.5` drew
   * the boundary between two biomes as a single unbroken line — organic in
   * shape, but one tile wide and absolute, which is the thing that reads as
   * jarring even when the line is not straight.
   *
   * Jittering the threshold with a high-frequency world hash turns that line
   * into a band a few tiles deep where both countries appear, thinning out on
   * each side: the dither every 16-bit overworld uses to change one ground
   * into another. It is world-space, so the band crosses seams, and it is
   * only on the MOISTURE axis — where the terrace changes there is a cliff,
   * and a cliff should change the country on one exact line. */
  /* AND THE PROMISE IS PLANTED WHERE THE WARP LOOKS (2026-09-18). A screen's country is read at its centre TILE,
   * and the read displaces that tile by the warp -- up to thirty tiles -- before asking which seed is nearest. So a
   * seed planted on the spawn tile could lose the opening screen to a country thirty tiles away: measured over
   * twenty worlds, two opened in scree and in fen, and on those the WHOLE opening screen was under the frost line
   * at night, which is the one thing this guarantee exists to prevent. The seed goes at the warped point, so the
   * read lands on it. */
  {
    const cx0 = start.x * COLS + (COLS >> 1), cy0 = start.y * ROWS + (ROWS >> 1);
    biomeSeeds.push({ b: mildSeed, x: cx0 + warpX(cx0, cy0), y: cy0 + warpY(cx0, cy0) });
    const sx1 = start.x * COLS + spx0, sy1 = start.y * ROWS + spy0;
    biomeSeeds.push({ b: mildSeed, x: sx1 + warpX(sx1, sy1), y: sy1 + warpY(sx1, sy1) });
  }
  const biomeAtTile = (wx, wy) => {
    const cx = Math.max(0, Math.min(terrain.TW - 1, wx));
    const cy = Math.max(0, Math.min(terrain.TH - 1, wy));
    const k = cy * terrain.TW + cx;
    const hit = biomeCache.get(k);
    if (hit) return hit;
    /* The warp is what stops this being a Voronoi diagram: without it the
     * borders are straight bisectors, which reads as a political map. Two
     * octaves at different scales bend them at country scale and fray them at
     * tile scale, and the fine one doubles as the dither that used to be a
     * separate jitter on the threshold. */
    const ax = cx + warpX(cx, cy), ay = cy + warpY(cx, cy);
    let best = biomeSeeds[0].b, bd = Infinity;
    for (const s2 of biomeSeeds) {
      const d = (ax - s2.x) * (ax - s2.x) + (ay - s2.y) * (ay - s2.y);
      if (d < bd) { bd = d; best = s2.b; }
    }
    biomeCache.set(k, best);
    return best;
  };
  /* WHERE TWO COUNTRIES MEET, THEY MIX.
   *
   * Both sides are plain GROUND, so no edge or corner rule can help — what
   * changes is the PALETTE, and a hard change of palette between (say) ash and
   * bloom is a 200-unit jump in hue and value along a one-tile line. It reads
   * as a border on a political map however ragged its shape.
   *
   * Near a border a tile takes a mixture of the two nearest countries, by how
   * close the second one is. Bucketed to eight steps so the baked-art cache
   * still hits — six biomes by six by eight by four terrace levels is bounded,
   * and in practice a screen touches a handful. */
  const biomeBlendAt = (wx, wy) => {
    const cx = Math.max(0, Math.min(terrain.TW - 1, wx));
    const cy = Math.max(0, Math.min(terrain.TH - 1, wy));
    const k = cy * terrain.TW + cx;
    const hit = blendCache.get(k);
    if (hit) return hit;
    const ax = cx + warpX(cx, cy), ay = cy + warpY(cx, cy);
    let b1: any = null, b2: any = null, d1 = Infinity, d2 = Infinity;
    for (const s2 of biomeSeeds) {
      const d = Math.hypot(ax - s2.x, ay - s2.y);
      if (d < d1) { d2 = d1; b2 = b1; d1 = d; b1 = s2.b; }
      else if (d < d2) { d2 = d; b2 = s2.b; }
    }
    // 0 well inside a country, rising to half at the border itself
    /* AND THEY INTERLEAVE RATHER THAN MIX.
     *
     * Blending the two palettes was tried first and it MUDDIES: ash is a
     * purple grey and dust is a tan, and half of each is neither — a screen on
     * that border came out with no colour in it at all. Mixing two authored
     * palettes produces colours that belong to no palette, which is the thing
     * a limited palette exists to prevent.
     *
     * Dithering keeps both pure. Near a border a tile takes one country or the
     * other, with the odds running from all-A to all-B across the seam, so the
     * two interleave over a few tiles the way a 16-bit overworld does it. The
     * hash is world-space, so the pattern crosses screens and does not crawl. */
    /* THE STRENGTH OF THE MIXTURE, spent as STIPPLE rather than as blend.
     *
     * Two wrong answers came first. Blending the palettes muddies — half of a
     * purple-grey ash and half of a tan dust is neither, and a screen on that
     * border lost all its colour. Choosing one country or the other per TILE
     * dithers at sixteen pixels, which is far too coarse: it came out as a
     * chessboard of whole tiles.
     *
     * A 16-bit overworld does this INSIDE the tile — the ground of one country
     * is stippled with the ground of the next, pixel by pixel, so both palettes
     * stay pure and the eye mixes them. That is what this number is for: how
     * strongly to stipple, quantised so the baked-art cache still hits. */
    const near = d2 > 0 ? d1 / d2 : 0;
    const t = Math.max(0, Math.min(1, (near - 0.80) / 0.20));
    const out = { a: b1, b: b2 || b1, t: Math.round(t * 4) / 8 };
    blendCache.set(k, out);
    return out;
  };

  // the screen-level name every older reader still asks for: what this screen
  // mostly is, taken at its middle
  const biome = [];
  for (let y = 0; y < WORLD_H; y++) {
    const row: any[] = [];
    for (let x = 0; x < WORLD_W; x++) row.push(biomeAtTile(x * COLS + (COLS >> 1), y * ROWS + (ROWS >> 1)));
    biome.push(row);
  }



  /* WHAT THIS WORLD'S PEOPLE PUT ON OPEN GROUND.
   *
   * Round 26 gave every otherwise-empty screen a wayshrine, and a
   * replayability review put the cost plainly: a landmark that appears on
   * 35% of screens of every seed is not a landmark, it is a texture, and it
   * is the same texture in all 400 worlds it measured. Two seeds differed in
   * where the shrines were and in nothing else about them.
   *
   * So a world has a CUSTOM, and it is drawn once from the seed: whether the
   * diggers here marked open ground with fire or with stone. `cairn` is the
   * share of marks that are cairns, and no world is pure — the rarer kind is
   * the one you remember finding.
   *
   *   shrines  a country of braziers; a cairn is a curiosity
   *   mixed    both, and neither tells you where you are
   *   cairns   stacked stone everywhere; a lit bowl is the surprise
   *
   * Paired with the density cut below, this is the axis the empty screen was
   * missing: not WHERE the mark is, which was already per-seed, but WHAT it
   * is and how often you see one. */
  const custom = [
    { name: 'shrines', cairn: 0.18 },
    { name: 'mixed', cairn: 0.50 },
    { name: 'cairns', cairn: 0.82 },
  ][new RNG(mix(seedNum, 4400)).int(3)];

  /* AND A WAY THROUGH, PROVEN IN WORLD SPACE.
   *
   * `ensureConnected` guarantees that each SCREEN is crossable, which is a
   * chunk-scoped promise and does not add up to a crossable world: measured on
   * seed t3, not one of the eight ramps could be reached holding all three
   * charms, because the wood closed the ground between the spawn and the first
   * of them and no per-screen pass could see that.
   *
   * So the trunk routes are laid here, where the whole map is in view. Within
   * a region every tile is the same terrace and connected by construction, so
   * a path always exists; what it has to route around is the sea, and what it
   * prefers to avoid is a river. Each region's waypoints — the spawn, and the
   * end of every ramp that touches it — are joined in turn, and the tiles of
   * those paths are handed to `generateScreen`, which clears the wood standing
   * on them. It never clears a cliff: the route goes through ramps, which is
   * what a ramp is for, so the tiering survives it. */
  const routes = new Set();
  {
    const waypoints = new Map();
    const add = (r, x, y) => {
      if (r < 0 || !regionTier.has(r)) return;
      if (!waypoints.has(r)) waypoints.set(r, []);
      waypoints.get(r).push([x, y]);
    };
    add(startRegion, start.x * COLS + spx0, start.y * ROWS + spy0);
    /* A RAMP'S OWN TILE BELONGS TO NO REGION — it is a cliff foot, and feet
     * are the walls between regions. So the route aims at the ground BESIDE
     * it, on each side. Getting this wrong meant no ramp was ever a waypoint
     * and the trunk routes led nowhere near the way up: on seed-8 the player
     * held every charm and reached tier 0 only, with all eight ramps correctly
     * cut and barred and none of them reachable. */
    for (const r of ramps) {
      for (const [dx, dy] of NEIGHBOURS) {
        const nx = r.x + dx, ny = r.y + dy;
        if (nx < 0 || ny < 0 || nx >= terrain.TW || ny >= terrain.TH) continue;
        add(regionId[terrain.at(nx, ny)], nx, ny);
      }
    }
    const TWt = terrain.TW, THt = terrain.TH;
    /* EVERY WAYPOINT IS ITSELF A ROUTE TILE.
     *
     * The paths below join waypoints in pairs, so a region holding only ONE —
     * a leaf plateau reached by a single ramp — got no path at all, and the
     * tile beside its ramp was never cleared. Regions are terrain: the tile
     * map can grow a wood on one, and then the ramp opens onto a tree. Traced
     * on seed-108: a tier-3 plateau of 255 tiles whose only ramp the player
     * reaches holding every charm, and cannot step off. */
    for (const [, pts] of waypoints) {
      for (const [px2, py2] of pts) routes.add(px2 + ',' + py2);
    }
    for (const [reg, pts] of waypoints) {
      for (let i = 1; i < pts.length; i++) {
        // breadth-first inside this region only, rivers taken last
        /* ...AND IT MAY NOT BE PATHED THROUGH A CLIFF FOOT.
         *
         * Being inside a region does not stop a tile being the foot of a step
         * up to the NEXT terrace — the foot belongs to the lower region, which
         * is this one. Those tiles are locked cliffs by the time a screen is
         * drawn, so a route laid across them cannot be cleared: measured on
         * seed t3, 85 of 441 route tiles were still impassable holding every
         * charm, and not one of the eight ramps could be reached.
         *
         * The path avoids feet, and only if there is no such path at all does
         * it fall back to allowing them — a route that has to cross a cliff is
         * better than no route, and `ensureConnected` still runs per screen. */
        const [gx, gy] = pts[i - 1];
        const [tx, ty] = pts[i];
        const goal = terrain.at(tx, ty);
        let found = -1, prev: any = null;
        for (let attempt = 0; attempt < 2 && found < 0; attempt++) {
          prev = new Int32Array(TWt * THt).fill(-2);
          prev[terrain.at(gx, gy)] = -1;
          const q: any[] = [[gx, gy]], slow: any[] = [];
          while ((q.length || slow.length) && found < 0) {
            const [cx, cy] = q.length ? q.shift() : slow.shift();
            for (const [dx, dy] of NEIGHBOURS) {
              const nx = cx + dx, ny = cy + dy;
              if (nx < 0 || ny < 0 || nx >= TWt || ny >= THt) continue;
              const k = terrain.at(nx, ny);
              if (prev[k] !== -2 || regionId[k] !== reg) continue;
              if (attempt === 0 && k !== goal && isStep(nx, ny)) continue;
              prev[k] = terrain.at(cx, cy);
              if (k === goal) { found = k; break; }
              (terrain.river[k] ? slow : q).push([nx, ny]);
            }
          }
        }
        for (let k = found; k >= 0; k = prev[k]) {
          routes.add((k % TWt) + ',' + Math.floor(k / TWt));
          if (prev[k] === -1) break;
        }
      }
    }
  }

  /* --- PUDDLES ARE NOT PONDS. A one-tile body of water is a stamp.
   *
   * Measured over three worlds, 66-100% of the inland water bodies were FOUR
   * TILES OR FEWER and the median was one or two: an art critic reading a
   * render of the whole island called it out at 0.95 confidence -- "water is
   * individual grid squares, hard axis-aligned edges, no bank, no rim, no depth
   * ramp" -- and a single wet tile in a meadow is exactly that however well the
   * shoreline is drawn, because there is no shoreline, only four corners.
   *
   * Wetness is decided by two WORLD arrays -- the filled heightmap against sea
   * level, and the river field -- so which tiles form a body can be answered
   * once here rather than per screen, where a pond straddling a seam would be
   * counted twice and neither half judged properly.
   *
   * Rivers survive by construction: they are long and connected, so a river is
   * never a small component. What goes is the isolated dimple, and it becomes
   * ordinary ground, which is what the land around it already is. */
  const puddle = new Set();
  {
    const TWp = terrain.TW, THp = terrain.TH;
    const isWetTile = (x, y) => {
      const j2 = terrain.at(x, y);
      if (isSea[j2]) return false;
      /* ...AND ROCK BREAKS A BODY, because the tile pass draws it over the
       * water. `terrain.river` runs on THROUGH a cliff -- the field does not
       * care -- while the screen puts CLIFF there and the river arrives as two
       * short pieces either side. Judged on the field alone, one long river was
       * one body and its rendered fragments were never small; asked the way the
       * screen draws it, they are what they look like. */
      if (terrain.face && terrain.face[j2] > 0) return false;
      return terrain.fill[j2] < terrain.sea || !!terrain.river[j2];
    };
    const seen = new Uint8Array(TWp * THp);
    const MINPOND = 5;
    for (let y = 0; y < THp; y++) {
      for (let x = 0; x < TWp; x++) {
        if (seen[y * TWp + x] || !isWetTile(x, y)) continue;
        const body = [];
        const q = [[x, y]];
        seen[y * TWp + x] = 1;
        while (q.length) {
          const [cx, cy] = q.pop();
          body.push([cx, cy]);
          for (const [dx, dy] of NEIGHBOURS) {
            const nx = cx + dx, ny = cy + dy;
            if (nx < 0 || ny < 0 || nx >= TWp || ny >= THp) continue;
            if (seen[ny * TWp + nx] || !isWetTile(nx, ny)) continue;
            seen[ny * TWp + nx] = 1;
            q.push([nx, ny]);
          }
        }
        /* A RIVER IS NEVER A PUDDLE. The terrace faces cut a river into reaches
         * a few tiles long, and this pass filled every short reach with ground:
         * a third of all river tiles came out dry, and the rivers read as rows
         * of puddles. A pond too small to be one is still filled. */
        if (body.length < MINPOND) for (const [bx, by] of body) if (!terrain.river[terrain.at(bx, by)]) puddle.add(bx + ',' + by);
      }
    }
  }

  const world0: any = { seedNum, terrain, regionId, regions: regionList, regionTier, ramps, routes, isSea, coast, landTiles, puddle,
    biomeSeeds, biomeAtTile, biomeBlendAt,
    links, biome, tier, start, dist, d1, d2, custom, island, islandCount,
    // the order this world hands out its charms, and therefore which barrier
    // stands at each tier of its borders
    order };
  /* --- NO HALF OF A PLATEAU NOTHING CAN ENTER. ---------------------------
   *
   * The gating proof labels regions over the terrain and joins them with
   * ramps; obstacles come later, per screen, and a belt of trees or boulders
   * can cut one region clean in two after the proof has called it one place.
   * Flooding four finished islands from spawn holding every charm found 567,
   * 293, 26 and 2 walkable tiles nobody could reach, and nearly all of them
   * sat in regions the flood had ALSO reached: one plateau, split by a belt.
   *
   * So the world is sealed here, once, before any screen is served. */
  world0.unseal = new Set();
  world0.drown = new Set();
  world0.ford = new Set();
  sealWorld(world0);
  /* ...AND THE COAST IS MEASURED AGAIN (LS-383, the merge): the seal drowns islets and pieces the flood could not
   * reach, and every painter that asks "how far is this water from the shore" -- the sea's shelf, the beach -- was
   * still answered by the terrain's coast, which had those tiles as land. play-1's screen 10,4 was open sea painted
   * as a shelving flat of the ash country. A drowned tile is water to the coast as well. */
  if (world0.drown.size) {
    const t = terrain, drown = world0.drown;
    world0.coast = coastField(t.TW, t.TH, t.at, (j) => t.fill[j] < t.sea || drown.has((j % t.TW) + ',' + Math.floor(j / t.TW)));
  }
  /* NO LINK OVER OPEN SEA (LS-383, the merge). The screen graph still joined screens across a strait, and every
   * planner that walks it -- the quest bot's worldRoute, the hunt's approach, a hunter's route -- planned a road over
   * water and wandered the coast for ever. An edge whose opening is water on both sides is no edge. */
  {
    const wetTile = (v) => v === T.SEA || v === T.WATER;
    for (const k of [...world0.links]) {
      const [a, b] = String(k).split('|').map((p) => p.split(',').map(Number));
      const dx = b[0] - a[0], dy = b[1] - a[1];
      // ...and no link touches a screen of open sea at all: a sea screen joined to two coasts was a road between islands
      if (world0.island && (world0.island[a[1] * WORLD_W + a[0]] < 0 || world0.island[b[1] * WORLD_W + b[0]] < 0)) { world0.links.delete(k); continue; }
      const ea = EDGES.find((e) => e.d[0] === dx && e.d[1] === dy), eb = EDGES.find((e) => e.d[0] === -dx && e.d[1] === -dy);
      if (!ea || !eb) continue;
      const ta = generateScreen(world0, a[0], a[1]).tiles, tb = generateScreen(world0, b[0], b[1]).tiles;
      if (ea.tiles().every(([x, y]) => wetTile(ta[y][x])) && eb.tiles().every(([x, y]) => wetTile(tb[y][x]))) world0.links.delete(k);
    }
  }
  return world0;
}

/** Cut every stranded piece of every region back onto reached ground.
 *
 * Generates all 64 screens (46ms) into a throwaway grid, floods from spawn
 * holding every charm, and for each connected piece of open ground the flood
 * did not reach cuts ONE tree or boulder on its ring -- preferring a ring tile
 * that touches reached ground, else any, so a belt two or three tiles thick
 * is crossed over a few rounds. Never a cliff, never water, never a tier
 * barrier: those are the gating, and the proof must keep hearing them. The
 * cuts go into `world.unseal`; `generateScreen`'s final sweep lifts them, so
 * the screens the game serves are the sealed ones. Pieces ringed only by
 * cliff and water are counted in `world.sealReport.residual` rather than
 * hidden. */
function sealWorld(world) {
  const ALL = { pods: 1, tide: 1, ember: 1 };
  const TW = WORLD_W * COLS, TH = WORLD_H * ROWS;
  const [spx, spy] = spawnTile(world.seedNum);
  const sx0 = world.start.x * COLS + spx, sy0 = world.start.y * ROWS + spy;
  /* WHAT THE SEAL MAY CUT, AS A LIST, NOT AS EVERYTHING-BUT.
   *
   * "Solid, not wet, not a barrier, not a cliff" admitted standing stones and
   * cairns -- and the screen sweep locks those, so the seal recorded a cut the
   * sweep then refused: wide-0 reported residual 0 with 465 tiles stranded, and
   * a comparison of ramp rules was made on that report. Trees and boulders are
   * the ordinary obstacles, and the sweep honours a cut of either even where a
   * lock covers the tile.
   *
   * ...AND A LUMP'S SKIRT. A cliff foot whose every higher neighbour is itself
   * rock is the ring around a lump, not the edge of a plateau: it guards no
   * tier. wide-27's 64-tile region is sealed by exactly 21 such feet. Cutting
   * one clears its terrain face too, so no wall is drawn over open ground. */
  const terr = world.terrain;
  const skirt = (x, y) => {
    const here = terr.terrace[terr.at(x, y)];
    let higher = 0;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
      const jn = terr.at(nx, ny);
      if (terr.terrace[jn] <= here) continue;
      higher++;
      // ask the grid, not the face: a lump's interior is rock with no drawn
      // face, and reading face here called every lump an open plateau
      if (grid[ny][nx] !== T.CLIFF) return false;
    }
    return higher > 0;
  };
  /* ...AND FRESH WATER, AS A SAND BAR. A river split the start region, and
   * bare-handed the far half -- Hollow I on it -- was 2220 tiles nobody could
   * reach: the cutter fells trees and boulders, and the ford rule lays only
   * through sea. A river tile on a cut path becomes a sand bar; a gate tile
   * (a tier's water in a corridor) never does. */
  const gateTiles = new Set();
  for (const r of world.ramps || []) for (const [gx, gy] of rampTiles(r)) gateTiles.add(gx + ',' + gy);
  const cuttable = (v, x, y) => v === T.TREE || v === T.ROCK || (v === T.CLIFF && skirt(x, y))
    || (isWet(v) && !gateTiles.has(x + ',' + y));
  let cut = 0, residual = 0, rounds = 0, drowned = 0, forded = 0;
  let grid = [];                                   // hoisted: `skirt` above reads it
  for (; rounds < 8; rounds++) {
    grid = [];
    for (let sy = 0; sy < WORLD_H; sy++) {
      const parts = [];
      for (let sx = 0; sx < WORLD_W; sx++) parts.push(generateScreen(world, sx, sy).tiles);
      for (let y = 0; y < ROWS; y++) {
        const row = [];
        for (let sx = 0; sx < WORLD_W; sx++) for (let x = 0; x < COLS; x++) row.push(parts[sx][y][x]);
        grid.push(row);
      }
    }
    /* TIERED, NOT ALL-CHARMS. Flooding with every charm called a tier-0 piece
     * reached when the only way to it ran through a tier-1 gate, and the
     * obstacle belt that split the start region bare-handed was never cut:
     * seed-178, Hollow I unreachable without a charm. Each round floods four
     * times -- with no charm, then the first, second and third of the
     * world's order -- and a stranded piece whose tier is within reach of
     * that many charms gets its path cut. */
    let cutThisRound = 0;
    residual = 0;
    for (let tierK = 0; tierK <= 3; tierK++) {
      const HAS: Record<string, number> = {};   // the charms held at this tier, filled in below
      for (let i = 0; i < Math.min(tierK, world.order.length); i++) HAS[world.order[i]] = 1;
        const seen = new Uint8Array(TW * TH);
      if (reachableTile(grid[sy0][sx0], HAS)) {
        const q = [[sx0, sy0]];
        seen[sy0 * TW + sx0] = 1;
        while (q.length) {
          const [x, y] = q.pop();
          for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
            const nx = x + dx, ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
            const k = ny * TW + nx;
            if (seen[k] || !reachableTile(grid[ny][nx], HAS)) continue;
            seen[k] = 1; q.push([nx, ny]);
          }
        }
      }
      const done = new Uint8Array(TW * TH);
            for (let y = 0; y < TH; y++) {
        for (let x = 0; x < TW; x++) {
          const k0 = y * TW + x;
          if (seen[k0] || done[k0] || !OPEN.has(grid[y][x])) continue;
          {   // only ground of a tier this many charms should reach
            const rr = world.regionId[world.terrain.at(x, y)];
            const rt = rr >= 0 ? world.regionTier.get(rr) : undefined;
            if (rt === undefined ? tierK < 3 : rt > tierK) continue;
          }
          const piece = [];
          const pq = [[x, y]];
          done[k0] = 1;
          while (pq.length) {
            const [px, py] = pq.pop();
            piece.push([px, py]);
            for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
              const nx = px + dx, ny = py + dy;
              if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
              const k = ny * TW + nx;
              // the piece is everything a full charm set can stand on, water included:
              // wide-0's strip was walled from the rest of its own region by one
              // row of shallows, and a piece of walkable tiles only stopped there
              if (done[k] || seen[k] || !reachableTile(grid[ny][nx], HAS)) continue;
              done[k] = 1; pq.push([nx, ny]);
            }
          }
          /* A PATH THROUGH WHAT MAY BE CUT, to the nearest reached ground.
           *
           * The first cut of this took one ring tile per round and, where none
           * touched reached ground, took any -- which on a piece walled off by
           * cliff chewed eight tiles inward on the far side and connected
           * nothing. A belt is crossed by a PATH: breadth-first out from the
           * piece through trees and boulders only, stopping at the first reached
           * tile, and every tile on that path is cut in one round. Where no such
           * path exists the wall is cliff or water, nothing is cut, and the piece
           * is a residual the report can show. */
          const prev = new Map();
          /* A CUT OR A FORD NEVER RUNS BESIDE HIGHER GROUND. A ford laid with the
           * tide to reach a tier-1 piece ran along the shore past a tier-2 beach,
           * and the tide alone walked ashore there (seed-211). A path tile may
           * not touch open ground of a tier above the flood's, unless that
           * ground is the piece itself. */
          const inPiece = new Set(piece.map(([px, py]) => py * TW + px));
          const tierAt = (x, y) => { const rr2 = world.regionId[world.terrain.at(x, y)]; return rr2 >= 0 ? world.regionTier.get(rr2) : undefined; };
          /* A PATH AT TIER k RUNS BETWEEN GROUND OF TIER k. Beside higher ground it
           * hands that ground to fewer charms than it costs; beside LOWER ground
           * at k >= 1 it is a bare-walkable bar or a felled tree between a tier-0
           * bank and a tier-k piece -- the very gate the region graph counts on,
           * bypassed (seed-296, seed-263: a tree cut for a tier-1 piece beside a
           * sand bar laid for a tier-0 pocket). At tier 0 only higher ground is
           * refused; above it, any open neighbour of another tier. */
          const besideHigher = (x, y) => {
            for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
              const ax = x + dx, ay = y + dy;
              if (ax < 0 || ay < 0 || ax >= TW || ay >= TH || inPiece.has(ay * TW + ax)) continue;
              if (!OPEN.has(grid[ay][ax])) continue;
              const rt2 = tierAt(ax, ay);
              if (rt2 === undefined) continue;
              if (rt2 > tierK) return true;
              if (tierK > 0 && tierK < 3 && rt2 < tierK) return true;   // with every charm held nothing is bypassed
            }
            return false;
          };
          // ...and it may only join reached ground of that tier (at tier 0, any)
          const hitOk = (x, y) => tierK === 0 || tierK === 3 || (tierAt(x, y) ?? 0) >= tierK;
          const bq = [];
          for (const [px, py] of piece) { prev.set(py * TW + px, -1); bq.push([px, py]); }
          let hit = null;
          while (bq.length && !hit) {
            const [px, py] = bq.shift();
            for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
              const nx = px + dx, ny = py + dy;
              if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
              const k = ny * TW + nx;
              if (prev.has(k)) continue;
              if (grid[ny][nx] === T.SEA) continue;   // THE SEA IS NOT CUT (LS-383): a stranded piece across a strait is another island, not a ford's far end -- route-1 wore shallow lines along the seams of every sea screen between
              if (seen[k]) { if (!hitOk(nx, ny)) continue; hit = [nx, ny]; prev.set(k, py * TW + px); break; }
              if (!reachableTile(grid[ny][nx], HAS) && !cuttable(grid[ny][nx], nx, ny)) continue;
              /* WATER IS CUT AS A SAND BAR ONLY BARE-HANDED. A sand bar is bare
               * ground; laid at tier 1 to reach a tier-1 piece it joined the
               * tier-0 bank it started from to the tier-1 bank it reached
               * (seed-173, -296, -263 walked a tier bare-handed). Above tier 0
               * a water cut is a ford -- shallow, the tide's -- and only when
               * the tide is among the charms this flood holds. */
              if (isWet(grid[ny][nx]) && !reachableTile(grid[ny][nx], HAS) && tierK > 0 && !HAS.tide) continue;
              if (besideHigher(nx, ny)) continue;
              prev.set(k, py * TW + px); bq.push([nx, ny]);
            }
          }
          if (hit) {
            // walk back from the reached tile to the piece, cutting the way
            let k = prev.get(hit[1] * TW + hit[0]);
            while (k !== undefined && k !== -1) {
              const cx = k % TW, cy = Math.floor(k / TW);
              if (cuttable(grid[cy][cx], cx, cy) && isWet(grid[cy][cx]) && tierK > 0 && !world.ford.has(cx + ',' + cy)) {
                world.ford.add(cx + ',' + cy); forded++; cutThisRound++;   // a ford, not a bar: see above
              } else if (cuttable(grid[cy][cx], cx, cy) && !world.unseal.has(cx + ',' + cy)) {
                world.unseal.add(cx + ',' + cy); cut++; cutThisRound++;
                if (grid[cy][cx] === T.CLIFF) {
                  const js = terr.at(cx, cy);
                  terr.face[js] = 0;
                  if (terr.crag) terr.crag[js] = 0;
                }
              }
              k = prev.get(k);
            }
          } else {
            /* NOT A PLACE, OR NOT YET JOINED.
             *
             * A piece nothing may cut into is one of three things. If every tile
             * of it sits BELOW sea level by the terrain's own `fill`, it is sea
             * the ocean flood missed -- 47 such tiles on contrast-1's east margin
             * -- and it becomes sea. The first cut of this drowned anything
             * ringed only by sea, and on wide-13 that drowned 41 tiles of real
             * land above sea level: the ring was the wrong test, the fill is the
             * right one.
             *
             * If it is real land across water, it gets a FORD: the shortest path
             * through sea to reached ground, laid as shallows. Shallows are
             * tide-gated, so a ford cannot hand out a tier -- and a piece the
             * region BFS never reached has no tier to hand out anyway.
             *
             * Ringed by cliff alone, it is counted. That case is a wall the
             * terrain labelled as one region, and it is fixed where the wall is
             * made, not here. */
            let belowSea = true;
            for (const [px, py] of piece) {
              if (world.terrain.fill[world.terrain.at(px, py)] >= world.terrain.sea) { belowSea = false; break; }
            }
            if (belowSea) {
              for (const [px, py] of piece) world.drown.add(px + ',' + py);
              drowned += piece.length; cutThisRound++;
            } else {
              // a ford: breadth-first through sea only, to the nearest reached tile
              const fprev = new Map();
              const fq = [];
              for (const [px, py] of piece) { fprev.set(py * TW + px, -1); fq.push([px, py]); }
              let fhit = null;
              while (fq.length && !fhit) {
                const [px, py] = fq.shift();
                for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
                  { const nx0 = px + dx, ny0 = py + dy; if (nx0 >= 0 && ny0 >= 0 && nx0 < TW && ny0 < TH && grid[ny0][nx0] === T.SEA) continue; }   // nor is a ford laid over the sea
                  const nx = px + dx, ny = py + dy;
                  if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
                  const k = ny * TW + nx;
                  if (fprev.has(k)) continue;
                  if (seen[k]) { if (!hitOk(nx, ny)) continue; fhit = [nx, ny]; fprev.set(k, py * TW + px); break; }
                  if (grid[ny][nx] !== T.SEA) continue;
                  if (besideHigher(nx, ny)) continue;
                  fprev.set(k, py * TW + px); fq.push([nx, ny]);
                }
              }
              if (fhit) {
                let k = fprev.get(fhit[1] * TW + fhit[0]);
                while (k !== undefined && k !== -1) {
                  const cx = k % TW, cy = Math.floor(k / TW);
                  if (grid[cy][cx] === T.SEA) { world.ford.add(cx + ',' + cy); forded++; }
                  k = fprev.get(k);
                }
                cutThisRound++;
              } else if (tierK === 3) residual += piece.length;   // a piece left at a lower tier gets its chance with more charms; residual is what the last flood cannot reach
            }
          }
        }
      }
    }
    /* A REEF WHERE THE TIDE WOULD LAND ON HIGHER GROUND. Water is region-less
     * and the tide swims all of it: a river or the coastal fringe ran straight
     * up to a tier-2 bank, and the tide alone walked ashore (seed-204, -282).
     * For each tier the flood is run holding that many charms, forbidden to
     * step onto ground above its tier; every water tile it can reach that
     * touches such ground becomes a boulder reef -- solid, and blasted only
     * with pods, which by then is charged at its own tier. */
    for (let tierK = 0; tierK <= 2; tierK++) {
      const HAS: Record<string, number> = {};   // the charms held at this tier, filled in below
      for (let i = 0; i < Math.min(tierK, world.order.length); i++) HAS[world.order[i]] = 1;
      if (!reachableTile(grid[sy0][sx0], HAS)) continue;
      const tierAt2 = (x, y) => { const rr = world.regionId[world.terrain.at(x, y)]; return rr >= 0 ? world.regionTier.get(rr) : undefined; };
      const seen2 = new Uint8Array(TW * TH); const q2 = [[sx0, sy0]]; seen2[sy0 * TW + sx0] = 1;
      let laid = 0;
      while (q2.length) {
        const [x, y] = q2.pop();
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const nx = x + dx, ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
          const k = ny * TW + nx;
          if (seen2[k] || !reachableTile(grid[ny][nx], HAS)) continue;
          const rt = tierAt2(nx, ny);
          if (rt !== undefined && rt > tierK) {
            // the step ashore: it is the WATER we stand in that becomes reef
            if (isWet(grid[y][x]) && !gateTiles.has(x + ',' + y) && !(world.reef && world.reef.has(x + ',' + y))) {
              (world.reef ||= new Set()).add(x + ',' + y); laid++; cutThisRound++;
            }
            continue;
          }
          seen2[k] = 1; q2.push([nx, ny]);
        }
      }
      void laid;   // the next round regenerates every screen with the reef in place
    }
    if (!cutThisRound) break;
  }
  world.sealReport = { rounds, cut, drowned, forded, residual };
}

function bfs(start, links) {
  const dist = new Map([[key(start.x, start.y), 0]]);
  const q = [start];
  while (q.length) {
    const c = q.shift();
    for (const [dx, dy] of DIRS) {
      const n = { x: c.x + dx, y: c.y + dy };
      if (n.x < 0 || n.y < 0 || n.x >= WORLD_W || n.y >= WORLD_H) continue;
      if (!links.has(edgeKey(c, n)) || dist.has(key(n.x, n.y))) continue;
      dist.set(key(n.x, n.y), dist.get(key(c.x, c.y)) + 1);
      q.push(n);
    }
  }
  return dist;
}

export function linked(world, x, y, dx, dy) {
  const nx = x + dx, ny = y + dy;
  if (nx < 0 || ny < 0 || nx >= WORLD_W || ny >= WORLD_H) return false;
  return world.links.has(edgeKey({ x, y }, { x: nx, y: ny }));
}


// Openings are 2 tiles wide, centred on each edge.
export const OPEN_X = [7, 8];
export const OPEN_Y = [5, 6];
/** Where the player physically stands when a run begins. */
export const SPAWN_TILE = [7, 5];
/** WHERE THE RUN BEGINS, PER SEED.
 *
 *  This was the constant above, so every run of every seed opened standing on
 *  the same square of the same screen — 8 distinct starts in the whole game,
 *  one per start screen. The glade is composed around this point and the
 *  standing stones are placed off it, so moving it moves the whole opening
 *  picture, not just the player.
 *
 *  Bounded well inside the border: the glade reaches about five tiles across
 *  and four down, so the anchor stays in x 4..10, y 4..7 and nothing it draws
 *  can reach a wall. That is 28 tiles x 8 start screens = 224 openings where
 *  there were 8 — measured, not asserted: `soak.mjs` counts 28 and 224 over
 *  200 seeds. (An earlier draft of this comment said x 4..11 / y 3..7 / 40 /
 *  320, which the line below has never produced.) `SPAWN_TILE` is kept as a
 *  representative value for anything that wants one. */
export function spawnTile(seedNum) {
  const rng = new RNG(mix(seedNum, 8181));
  return [4 + rng.int(7), 4 + rng.int(4)];
}

/* The middle of each screen edge, and the lane behind it: the seam pass paves these on a linked edge. A generator
 * shape left from the walled flip-screen screens -- it cuts the same two tiles on every edge of every screen -- kept
 * for now because the worlds (and every save) are made with it; LS-511's generator stage replaces it. (`gate`, `jamb`
 * are the old barrier's cells; only `tiles`, `mouth`, `inner` and `anchor` are read.) */
const EDGES = [
  { d: [0, -1], tiles: () => OPEN_X.map(x => [x, 0]),
    gate: () => OPEN_X.map(x => [x, 2]),
    mouth: () => OPEN_X.map(x => [x, 1]),
    jamb: () => [[OPEN_X[0] - 1, 1], [OPEN_X[0] - 1, 2],
      [OPEN_X[OPEN_X.length - 1] + 1, 1], [OPEN_X[OPEN_X.length - 1] + 1, 2]],
    inner: () => OPEN_X.flatMap(x => [[x, 3], [x, 4]]), anchor: [OPEN_X[0], 3] },
  { d: [0, 1], tiles: () => OPEN_X.map(x => [x, ROWS - 1]),
    gate: () => OPEN_X.map(x => [x, ROWS - 3]),
    mouth: () => OPEN_X.map(x => [x, ROWS - 2]),
    jamb: () => [[OPEN_X[0] - 1, ROWS - 2], [OPEN_X[0] - 1, ROWS - 3],
      [OPEN_X[OPEN_X.length - 1] + 1, ROWS - 2], [OPEN_X[OPEN_X.length - 1] + 1, ROWS - 3]],
    inner: () => OPEN_X.flatMap(x => [[x, ROWS - 4], [x, ROWS - 5]]), anchor: [OPEN_X[0], ROWS - 4] },
  { d: [-1, 0], tiles: () => OPEN_Y.map(y => [0, y]),
    gate: () => OPEN_Y.map(y => [2, y]),
    mouth: () => OPEN_Y.map(y => [1, y]),
    jamb: () => [[1, OPEN_Y[0] - 1], [2, OPEN_Y[0] - 1],
      [1, OPEN_Y[OPEN_Y.length - 1] + 1], [2, OPEN_Y[OPEN_Y.length - 1] + 1]],
    inner: () => OPEN_Y.flatMap(y => [[3, y], [4, y]]), anchor: [3, OPEN_Y[0]] },
  { d: [1, 0], tiles: () => OPEN_Y.map(y => [COLS - 1, y]),
    gate: () => OPEN_Y.map(y => [COLS - 3, y]),
    mouth: () => OPEN_Y.map(y => [COLS - 2, y]),
    jamb: () => [[COLS - 2, OPEN_Y[0] - 1], [COLS - 3, OPEN_Y[0] - 1],
      [COLS - 2, OPEN_Y[OPEN_Y.length - 1] + 1], [COLS - 3, OPEN_Y[OPEN_Y.length - 1] + 1]],
    inner: () => OPEN_Y.flatMap(y => [[COLS - 4, y], [COLS - 5, y]]), anchor: [COLS - 4, OPEN_Y[0]] },
];

/** The apron tile just inside an opening — carving targets these, never the
 *  border tiles themselves, which a corridor can't cut through. */

export function anchorFor(dx, dy) {
  return EDGES.find(e => e.d[0] === dx && e.d[1] === dy).anchor;
}



/* Every tile a ramp occupies: the run from its near end to its far end (one
 * tile for a wall one thick, up to five through a stacked band -- see the
 * corridor scan in `buildRegions`), and the second tile of a wide one. */
export function rampTiles(r) {
  const out = [];
  const x2 = r.x2 ?? r.x, y2 = r.y2 ?? r.y;
  const dx = Math.sign(x2 - r.x), dy = Math.sign(y2 - r.y);
  const n = Math.max(Math.abs(x2 - r.x), Math.abs(y2 - r.y));
  for (let i = 0; i <= n; i++) out.push([r.x + dx * i, r.y + dy * i]);
  if (r.wide) out.push(r.wide);
  return out;
}

/* FORDS (LS-225 step 0). With the charms gone the sea was the last gate: on
 * most worlds a few screens lay across open water from every linked
 * neighbour, wadeable only with the tide charm. Where a linked edge opens
 * onto deep water, a ford of shallows is cut from the opening across the
 * water to the nearest land on this screen, two tiles wide, so the crossing
 * is a wade. Deterministic: the search order is fixed and reads only tiles. */
export function fordSeaEdges(world, sx, sy, t) {
  const wet = (v) => v === T.SEA || v === T.WATER;
  const land = (v) => walkableTile(v, {});
  /* NOT ON THE OPEN SEA (LS-383; Vijay: "what's with all the rectangular paths across the water"). A screen with no
   * land on it took a shallow path along every edge so the lung could cross it edge to edge, and on the one island
   * those hugged the map's rim; between islands they drew a grid over the straits. A strait is crossed by the
   * cutter's fords, or by ice, the lung's hop and a raft -- not by a seam. */
  { let any = false; for (let y = 0; y < ROWS && !any; y++) for (let x = 0; x < COLS; x++) if (land(t[y][x])) { any = true; break; } if (!any) return; }
  for (const e of EDGES) {
    const [dx, dy] = e.d;
    if (!linked(world, sx, sy, dx, dy)) continue;
    /* ...AND NOT TOWARD THE OPEN SEA EITHER (LS-383, the merge): a coast screen still cut a stub of shallows at
     * every edge that faced a screen of nothing but sea -- the dashes ringing every island on the map. */
    if (world.island && world.island[(sy + dy) * WORLD_W + (sx + dx)] === -1) continue;
    /* ...NOR TOWARD ANOTHER ISLAND: two islands' corners on facing screens were seamed straight across the strait. */
    if (world.island && world.island[sy * WORLD_W + sx] >= 0 && world.island[(sy + dy) * WORLD_W + (sx + dx)] !== world.island[sy * WORLD_W + sx]) continue;
    const opening = e.tiles();
    if (!opening.some(([x, y]) => wet(t[y][x]))) continue;
    // the goal is land, or the opening of another linked edge (a screen that is all sea is crossed edge to edge)
    const others = new Set<string>();
    for (const o of EDGES) { if (o === e || !linked(world, sx, sy, o.d[0], o.d[1])) continue; for (const [x, y] of o.tiles()) others.add(x + ',' + y); }
    const wasWet = opening.map(([x, y]) => wet(t[y][x]));
    for (const [x, y] of opening) if (wet(t[y][x])) t[y][x] = T.SHALLOW;
    const start = opening[0];
    const seen = new Set<string>(opening.map(([x, y]) => x + ',' + y)); const prev = new Map<string, string>();
    const q: [number, number][] = opening.map(([x, y]) => [x, y]);
    let goal: [number, number] | null = null;
    while (q.length && !goal) {
      const [x, y] = q.shift()!;
      for (const [ddx, ddy] of [[-dx, -dy], [1, 0], [-1, 0], [0, 1], [0, -1]]) {   // inward first
        const nx = x + ddx, ny = y + ddy;
        if (nx < 0 || ny < 0 || nx >= COLS || ny >= ROWS) continue;
        const k = nx + ',' + ny; if (seen.has(k)) continue;
        const v = t[ny][nx];
        if (land(v) || others.has(k)) { goal = [nx, ny]; prev.set(k, x + ',' + y); break; }
        if (!wet(v) && v !== T.SHALLOW) continue;
        seen.add(k); prev.set(k, x + ',' + y); q.push([nx, ny]);
      }
    }
    void start; void wasWet;
    if (!goal) continue;   // water with no land on this screen: the neighbour's ford meets it
    let k: string | undefined = goal.join(',');
    while (k) {
      const [x, y] = k.split(',').map(Number);
      if (wet(t[y][x])) t[y][x] = T.SHALLOW;
      const sxn = dx ? x : x + 1, syn = dx ? y + 1 : y;   // two wide, across the edge's axis
      if (sxn >= 0 && syn >= 0 && sxn < COLS && syn < ROWS && wet(t[syn][sxn])) t[syn][sxn] = T.SHALLOW;
      k = prev.get(k);
    }
  }
}

export function generateScreen(world, sx, sy) {
  /* A SCREEN'S RECORDS ARE ITS OWN GENERATION'S. The seal generates every
   * screen a round at a time before the game does, and each pass here writes
   * to world-level maps keyed by screen; a stale entry from a seal round then
   * told a later pass the screen already had its stone (so none was placed)
   * and the guard took the phantom back. read-28 placed 58 gate stones and
   * kept 4. Every record for this screen is dropped before the passes run. */
  {
    const pre = arguments[1] + ',' + arguments[2] + '|';
    if (world.stones) for (const k of [...world.stones.keys()]) if (k.startsWith(pre)) world.stones.delete(k);
    if (world.stoneWhy) world.stoneWhy = world.stoneWhy.filter((r) => !(r.sx === arguments[1] && r.sy === arguments[2]));
    if (world.markWhy) world.markWhy.delete(arguments[1] + ',' + arguments[2]);
  }
  const biomeName = world.biome[sy][sx];
  const pal = biomeName;
  const rng = new RNG(mix(world.seedNum, sx, sy, 31));
  /* A TIER LINE is a tile whose neighbours belong to regions of different
   * tiers (or to a region and to none): paving it joins what a charm was
   * meant to part. Every pass that fords water or clears a forecourt asks. */
  const tierLine = (lx, ly) => {
    const tiers = new Set();
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const wx = sx * COLS + lx + dx, wy = sy * ROWS + ly + dy;
      if (wx < 0 || wy < 0 || wx >= world.terrain.TW || wy >= world.terrain.TH) continue;
      const rr = world.regionId[world.terrain.at(wx, wy)];
      if (rr >= 0 && world.regionTier.has(rr)) tiers.add(world.regionTier.get(rr));
    }
    return tiers.size > 1;
  };
  const t = [];
  for (let y = 0; y < ROWS; y++) t.push(new Array(COLS).fill(T.GROUND));

  const wallOf = (b) => (b === 'scree' || b === 'ash' ? T.ROCK : T.TREE);
  const wallTile = wallOf(biomeName);

  // --- WATER IS AMBIENT, AND THAT MAKES IT AN UNFAIR LAST CHARM.
  //
  // Rubble and thorns are discrete barriers: holding their charm back closes
  // exactly the tiles they occupy. Deep water is terrain, so withholding the
  // tide strands the LAND behind it too. Measured at two of three charms:
  // tide-last leaves 64.8%-72.7% of the world walkable, every other order
  // 100.0%. `gate.mjs` cannot see this — it asks whether the next hollow is
  // reachable, never how much of the world is.
  //
  // AN ORDER-AWARE WATER BAND WAS TRIED AND REVERTED. Raising the cut to
  // 0.760 for tide-last worlds worked on its own terms: +8 points (to
  // 73.4%-80.1%), water down from 11.0% to 7.2% of tiles, gate green at
  // 3,000. It also put two softlocks back within reach — `a launch never
  // carries you off the screen` went to 1 of 1519, and `a launch over a gate
  // lands somewhere, not in a pocket` to 1 stuck of 10 — and both failures
  // disappeared again the moment the band went back. More open land means
  // launches fly further, and the seam and pocket guards turn out to be
  // sensitive to how much of a screen is walkable.
  //
  // That sensitivity is the real defect and it is worth fixing on its own:
  // those guards should hold whatever the terrain does. Until they do, an
  // experiential gain is not worth a reachable softlock, so the band stays
  // uniform and the gap is measured rather than papered over.
  const ox = sx * COLS, oy = sy * ROWS;
  const terr = world.terrain;
  /* Which biome each TILE is, so the picture can change where the land does
   * rather than where the chunk ends. `biomeName` above is still this screen's
   * majority, because the density habits and the spawn tables are per screen
   * and have no reason not to be. */
  /** The foot of a terrace step. These are the world's cliffs and no later
   *  pass may thin them — see the cull below. */
  const cliffAt = new Set();
  const bTiles: any[] = [];
  for (let y = 0; y < ROWS; y++) {
    const row: any[] = [];
    for (let x = 0; x < COLS; x++) row.push(world.biomeAtTile(ox + x, oy + y));
    bTiles.push(row);
  }
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      const wx = ox + x, wy = oy + y;
      const wet = fbm(world.seedNum + 11, wx, wy, 0.055);
      const wood = fbm(world.seedNum + 733, wx, wy, 0.085);
      // A NARROWER BAND WHEN THE TIDE COMES LAST — AND ONLY AS FAR AS THE
      // SOFTLOCK CHECKS ALLOW.
      //
      // Deep water is ambient, so withholding its charm strands the land
      // behind it as well as itself; rubble and thorns close only the tiles
      // they occupy. The first attempt at this used 0.760, which fixed the
      // openness and put two launch softlocks back within reach — a launch
      // flies further over open land — so it was reverted whole.
      //
      // That was the right call and the wrong resolution: the guard was
      // averaging per ORDER, so it could not see that one seed in sixty
      // opened at 29.2% walkable while its order averaged 64.8%. With a
      // per-seed floor in place the target is the tail, not the mean, and the
      // tail moves at a much smaller step: 0.745 takes the worst seed from
      // 29.2% to 36.3% and leaves `a launch never carries you off the screen`
      // and `a launch over a gate lands somewhere` both green, where 0.760
      // failed them at 1 of 1519 and 1 stuck of 10.
      /* THE GROUND COMES FROM THE GROUND, not from a second noise field.
       *
       * Water was `fbm(seedNum + 11)` and woods `fbm(seedNum + 733)`, neither
       * of which had ever seen the heightmap — so the terrain said where the
       * sea and the rivers were while this pass independently invented lakes
       * somewhere else, and the cliff faces drawn from the terraces lined up
       * with nothing. Two worlds occupying the same tiles.
       *
       * Now there is one. The sea is where the land is below sea level, a
       * river is where the flow accumulated, and a cliff is the foot of a
       * terrace step — the tile you cannot walk INTO, which is the lower one,
       * because the face is drawn across the top of it.
       *
       * Rivers are SHALLOW, deliberately: a river should read as a river and
       * be forded, not stand in for the tide barrier. The sea is what the
       * tide opens. */
      const j = terr.at(wx, wy);
      const h = terr.fill[j];
      const gj = (fbm(world.seedNum + 5507, wx, wy, 0.9) - 0.5) * 0.09;
      const lvl = terr.terrace[j];
      /* THE ROCK IS WHERE THE TERRAIN SAYS IT IS.
       *
       * This worked the cliff out again from the terraces, which was the same
       * answer as `terrain.face` right up until faces got a HEIGHT: a wall
       * three tiles high claims three tiles of ground, `buildRegions` labels
       * the plateaus from that, and this pass went on drawing one. The regions
       * then believed in rock the tiles did not have, and the gating proof
       * went to 60 failures in 61 seeds. One array, asked by everyone. */
      const cliff = terr.face ? terr.face[j] > 0 : false;
      void lvl;
      // a body too small to be a body — see `puddle` in `generateWorld`
      const dimple = world.puddle && world.puddle.has(wx + ',' + wy);
      /* THE BEACH TEST, HOISTED, because two branches need it.
       *
       * `dimple` fills a body of water too small to be a body -- and on a coast
       * those are everywhere, so it was claiming the waterline and painting it
       * plain ground BEFORE the beach branch further down could be reached.
       * Measured over 249 coast crossings: 60% had no sand, and of those, 98 of
       * 149 were GROUND or MOSS, which are exactly the two kinds this branch
       * paints. Tripling the beach threshold moved it by three percentage
       * points, which is what said the threshold was not the constraint. */
      /* ...AND THE WATERLINE MOVES SLOWLY, or there is no band to see.
       *
       * The second octave is at scale 0.19 -- a wavelength of about five tiles
       * -- with an amplitude of 2.6, which is comparable to the whole depth of
       * a beach. Adjacent tiles therefore compare their coast distance against
       * materially different thresholds, and the ordering INVERTS: walking in
       * from the sea on one row gave sand at coast 3, GROUND at coast 2, sand
       * at coast 1. A band broken every other tile is not a band, and it is why
       * the coast read as having no beach even where sand was being placed.
       *
       * The fine octave is what stops a beach ending in a clean arc, so it
       * stays -- at an amplitude small enough that it ruffles the waterline
       * instead of perforating it. */
      const beachy = !!(world.coast && world.coast[j] <= 3.1
        + (fbm(world.seedNum + 9311, wx, wy, 0.055) - 0.5) * 7.5
        + (fbm(world.seedNum + 9317, wx, wy, 0.19) - 0.5) * 0.8);
      if (world.isSea && world.isSea[j]) t[y][x] = T.SEA;
      else if (dimple) {
        t[y][x] = beachy ? T.SAND
          : (terr.moist[j] + gj > 0.5 ? T.MOSS : T.GROUND);
      }
      else if (h < terr.sea - 0.03) t[y][x] = T.WATER;
      else if (h < terr.sea) t[y][x] = T.SHALLOW;
      else if (cliff) { t[y][x] = T.CLIFF; cliffAt.add(x + ',' + y); }
      else if (terr.river[j]) t[y][x] = T.SHALLOW;
      else if (wood > 0.655) t[y][x] = wallOf(bTiles[y][x]);
      else if (wood > 0.630 && rng.chance(0.35)) t[y][x] = wallOf(bTiles[y][x]);
      // the same dither on the ground cover, so sand does not end on a contour
      // either — `EDGED` gives these two a rim, and a rim on a ragged edge is
      // a shoreline where a rim on a clean one is a border
      /* A BEACH, from the coast distance field rather than from a moisture
       * threshold. Sand happened to appear near water before only when the
       * moisture noise agreed; now the shore has one because it is the shore.
       *
       * ...AND IT IS A BEACH RATHER THAN AN OUTLINE. The threshold was the
       * constant 1.6, so the sand traced the coast at one width the whole way
       * round: measured over three worlds, 75 of 144 crossings were ONE tile
       * deep and 48 were two, median 1. An art critic reading a render of the
       * whole island named it without being pointed at it -- "a uniform 1-tile
       * brown band that traces the coast at constant width, an outline, not a
       * beach" -- and it is the same fault as a ruled seam one scale up.
       *
       * A slow world field moves the waterline in and out instead, so sand
       * piles up in the bays and thins to nothing on the headlands. Two
       * octaves: the broad one makes beaches and rocky stretches a dozen tiles
       * long, the finer one stops their ends being clean arcs. In world
       * coordinates, so a beach runs across a screen seam like everything else
       * here. */
      /* ...AND THE SWING WAS BIGGER THAN THE BEACH.
       *
       * Base 1.6 with a broad octave of amplitude 11 spends most of its range
       * BELOW ZERO, and a negative threshold is no sand at all. Measured over
       * two worlds and 249 coast crossings: 74% had no beach whatsoever and the
       * mean depth was 0.6 tiles. The fix for "a uniform one-tile band" had
       * removed the band rather than varied it, and the island came out with
       * the land simply stopping at the sea — cut with scissors.
       *
       * A deeper base and a tighter swing keeps what the variation was for —
       * sand piling in the bays, thinning on the headlands — while leaving most
       * of the coast with a shore on it. */
      else if (beachy) t[y][x] = T.SAND;
      else if (terr.moist[j] + gj < 0.32) t[y][x] = T.SAND;
      else if (terr.moist[j] + gj > 0.62 || wood > 0.555) t[y][x] = T.MOSS;
    }
  }
  // Regional noise can bury a whole screen in obstacles; thin it back so a
  // screen always reads as a place rather than a maze.
  /* THE HABIT GOVERNS THE PLAY AREA, NOT THE FRAME.
   *
   * The cull ran over rows 1..ROWS-1 and the TREELINE thickens that band
   * afterwards, biome-blind, adding a near-constant amount to every screen.
   * Dense biomes therefore landed on their target and sparse ones could not:
   * measured on the generator's own window, fen 22.0% against 21.5% asked and
   * bloom 25.0% against 25.0%, while ash came out 21.7% against 13.0% and
   * dust 23.3% against 15.0% — the sparse half overshooting by 8+ points and
   * the whole spread collapsing to 3.9 where 12 was asked.
   *
   * Two ways out: make the treeline biome-aware, or stop asking the habit to
   * account for it. The treeline exists so no screen reads as a walled
   * rectangle — that is a framing decision and it should not be traded for a
   * density number — so the habit now governs the inner 12x8, which is the
   * part of the screen the player actually moves through, and the band is
   * left alone.
   */
  /* A RIVER RUNS DEEP WHERE IT WIDENS. Every river tile was drawn shallow,
   * and with no inland lake the world had no deep water at all: nothing to
   * swim, and no waterline for the thicket pass to grow a hedge on (0 hedges
   * in 40 worlds). A river tile whose four neighbours are river, none of
   * them a face, is deep water; asked of the terrain, so both sides of a
   * seam agree. */
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      if (t[y][x] !== T.SHALLOW) continue;
      const wx = ox + x, wy = oy + y;
      if (!terr.river[terr.at(wx, wy)]) continue;
      let deep = true;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = wx + dx, ny = wy + dy;
        if (nx < 0 || ny < 0 || nx >= terr.TW || ny >= terr.TH) { deep = false; break; }
        const jn = terr.at(nx, ny);
        if (!terr.river[jn] || (terr.face && terr.face[jn])) { deep = false; break; }
      }
      if (deep) t[y][x] = T.WATER;
    }
  }
  const interior = (COLS - 4) * (ROWS - 4);
  const cap = Math.floor(interior * 0.26);
  /* A CLIFF IS NOT SCENERY AND THE DENSITY CULL MAY NOT EAT IT.
   *
   * The cull collects every TREE and ROCK as `blocked` and thins them to the
   * biome's density target, replacing the losers with MOSS or GROUND. Terrace
   * feet are ROCK, so the entire cliff system was being deleted a few tiles
   * at a time the moment it was built: rendered at 1:1 the terraces showed as
   * flat changes of colour with no face anywhere, which read exactly like the
   * world having no height at all.
   *
   * They are terrain, like water, and the passes that shape scenery leave
   * them alone. */
  /* THE CLIFFS ARE LOCKED HERE, NOT LATER.
   *
   * `locked` was declared four hundred lines down, so every pass between the
   * base terrain and it could paint over a terrace foot — and two of them did.
   * The waterline thicket pass turns tiles into `T.THORN`, and a thorn is
   * opened by the ember charm, so a foot that became one was a way up the
   * cliff for whoever held ember. Traced on seed-11: `GROUND(t1) -> THORN ->
   * SCORCH(t2)`, a tier crossed by burning one bush.
   *
   * The set exists from the moment the cliffs do. The ramp pass below still
   * writes its barriers into it deliberately; nothing else may touch them. */
  const locked = new Set();
  for (const k of cliffAt) locked.add(k);
  /* ...AND THE WATER, WHICH IS TERRAIN TOO.
   *
   * `carve` writes GROUND over whatever is in its way to connect a screen, and
   * a pond was not on its do-not-touch list — so it drained one and left a
   * land bridge where the map has water. That is a way from one plateau to
   * another that no charm was asked for: traced on seed-18, GROUND at 118,74
   * and 118,75 with `fill` below sea level on both, joining a tier-0 region to
   * a tier-2 one. The trunk routes guarantee the world is crossable now, so a
   * screen that cannot be crossed internally is a screen you walk round. */
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      if (terr.fill[terr.at(ox + x, oy + y)] < terr.sea) locked.add(x + ',' + y);
    }
  }

  const blocked = [];
  for (let y = 2; y < ROWS - 2; y++) for (let x = 2; x < COLS - 2; x++) {
    if (cliffAt.has(x + ',' + y)) continue;
    if (t[y][x] === T.TREE || t[y][x] === T.ROCK) blocked.push([x, y]);
  }
  /* SIX BIOMES, SIX SHAPES — NOT ONE SCATTER RECOLOURED.
   *
   * This thinned by `rng.shuffle`, which is the whole problem. Whatever shape
   * the regional noise had produced, a uniform random cull destroyed it, so
   * every biome converged on the same statistical texture and differed only
   * in palette. Two reviewers reached that independently from opposite ends:
   * the art lens measured interior obstacle density across all six at
   * 17.2-18.7% — a 1.5 point spread — and called the biome system "a recolour,
   * not a level grammar"; the design lens measured 45.8% of wild screens
   * carrying no authored feature at all. Both scored 4, both for four rounds.
   *
   * The density was never the problem. 17.9% is a good number. What matters
   * is where those tiles LAND, so the cull is scored instead of shuffled: each
   * biome keeps the obstacles that fit its own habit and drops the ones that
   * do not.
   *
   *   scree  clumps hard   — boulder fields you round, not gravel you cross
   *   ash    isolates      — burnt stumps standing alone in the open
   *   fen    clumps at water — bramble islands in the shallows
   *   dust   runs east-west — ridge lines, so the eye has a horizon
   *   heath  scatters      — open moor, which is what it was
   *   bloom  clumps softly — thickets with lanes between them
   *
   * The score is deliberately weak (a small random term is added) so the
   * result reads as a tendency rather than a stencil, and the cap above is
   * untouched: this changes the SHAPE of the 26%, never the amount.
   */
  const HABIT = {
    scree: { clump: 2.4, water: 0, axis: 0, want: 0.235 },
    ash: { clump: -1.8, water: 0, axis: 0, want: 0.130 },
    fen: { clump: 1.1, water: 2.2, axis: 0, want: 0.215 },
    dust: { clump: 0.2, water: 0, axis: 3.4, want: 0.150 },
    heath: { clump: -0.7, water: 0, axis: 0, want: 0.175 },
    bloom: { clump: 1.5, water: 0, axis: 0, want: 0.250 },
  };
  /* ...AND IT RUNS ON EVERY SCREEN, NOT JUST THE CROWDED ones.
   *
   * Scoring the cull did nothing at first, because the cull only fired when
   * the regional noise overshot the cap — measured, 491 of 1,300 screens,
   * 37.8%. On the other 62% the shape was whatever the noise happened to
   * make, which is the same noise in every biome. A habit that applies to a
   * third of the country is not a habit.
   *
   * So each biome names the density it WANTS and the cull always runs to it.
   * This is purely subtractive — it only ever turns TREE/ROCK into GROUND or
   * MOSS — so it cannot disconnect anything, which is what makes it safe to
   * run everywhere without re-deriving the gating proof.
   */
  const habitTarget = Math.floor(interior * ((HABIT[biomeName] || HABIT.heath).want));
  if (blocked.length > habitTarget) {
    const habit = HABIT[biomeName] || HABIT.heath;
    const solidAt = (x, y) => t[y] !== undefined && t[y][x] !== undefined
      && (t[y][x] === T.TREE || t[y][x] === T.ROCK);
    const wetAt = (x, y) => t[y] !== undefined && t[y][x] !== undefined
      && isWet(t[y][x]);
    const scored = blocked.map(([x, y]) => {
      let near = 0, sideways = 0, damp = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (!dx && !dy) continue;
          if (solidAt(x + dx, y + dy)) { near++; if (dy === 0) sideways++; }
          if (wetAt(x + dx, y + dy)) damp++;
        }
      }
      const score = habit.clump * near + habit.axis * sideways
        + habit.water * damp + rng.range(0, 1.1);
      return { x, y, score };
    });
    // lowest scores are the ones that do not belong to this country
    scored.sort((a, b) => a.score - b.score);
    for (let i = 0; i < scored.length - habitTarget; i++) {
      const { x, y } = scored[i];
      t[y][x] = rng.chance(0.35) ? T.MOSS : T.GROUND;
    }
  }
  // ...and a floor as well as a ceiling, so no screen is a bare rectangle with
  // nothing to route around or navigate by
  /* ...AND THE FLOOR AIMS AT THE BIOME'S OWN TARGET.
   *
   * This topped up to a flat 9%, so a biome that ASKS to be dense could only
   * ever get what the regional noise happened to give it: measured, bloom
   * asked 25.0% and delivered 17.0%, scree asked 23.5% and delivered 15.2%,
   * while ash and dust landed within 2 points of theirs. The cull can only
   * ever take away, so without a floor that follows the habit the dense half
   * of the table is decided by noise and the sparse half by design.
   *
   * A little under the target rather than at it, so the pass tops a screen up
   * without flattening the variation the noise provides. */
  const floorCount = Math.floor(interior
    * Math.max(0.06, (HABIT[biomeName] || HABIT.heath).want - 0.05));
  let have = Math.min(blocked.length, habitTarget);
  let guard = 0;
  /* ...AND WHAT IT PUTS BACK WEARS THE SAME HABIT.
   *
   * This always laid a 1-to-3 by 1-to-3 rectangle, which is a clump whatever
   * the country is — so on ash and heath, whose whole character is standing
   * alone in the open, the floor pass quietly undid the cull that had just
   * run. A biome that isolates gets single tiles; one that clumps gets a
   * block; one that runs east-west gets a bar.
   */
  const shape = () => {
    const h2 = HABIT[biomeName] || HABIT.heath;
    if (h2.clump < 0) return [1, 1];
    if (h2.axis > 1) return [rng.irange(2, 4), 1];
    return [rng.irange(1, 3), rng.irange(1, 3)];
  };
  while (have < floorCount && guard++ < 60) {
    const bx = rng.irange(2, COLS - 4), by = rng.irange(2, ROWS - 4);
    const [bw, bh] = shape();
    for (let y = by; y < Math.min(by + bh, ROWS - 1); y++) {
      for (let x = bx; x < Math.min(bx + bw, COLS - 1); x++) {
        /* NOT ON THE BEACH.
         *
         * This pass plants rock until the screen hits its country's obstacle
         * density, and it took SAND as fair game along with ground and moss --
         * so the shore, which is a two or three tile strip and the most-seen
         * edge in the game, was one of the places it quarried. Counted over two
         * worlds: the terrain asked for 5,437 sand tiles and 2,922 survived to
         * the end of `generateScreen`. Forty-six per cent of every beach was
         * being turned into boulders to make a density target, which is why
         * three rounds of widening the beach threshold moved the picture by
         * almost nothing.
         *
         * A screen with a lot of shore now carries fewer boulders, which is
         * what a beach looks like. */
        if (t[y][x] !== T.GROUND && t[y][x] !== T.MOSS) continue;
        t[y][x] = rng.chance(0.5) ? T.ROCK : wallTile;
        have++;
      }
    }
  }

  /* A FEW BOULDERS FOR TEXTURE — AND THEY OBEY THE COUNTRY THEY ARE IN.
   *
   * This ran after `habitTarget` and knew nothing about it, so it put back
   * what the cull had just taken out. An art reviewer measured the delivered
   * interior density against what each biome ASKS for: ash wants 13.0% and
   * measured 22.65%, dust wants 15.0% and measured 22.99% — the four sparse
   * biomes compressed into a 1.2-point band, which is the same convergence
   * round 18 was written to end. Intended spread 12 points, delivered 7.5.
   *
   * A biome that isolates gets fewer and smaller clumps; one that clumps gets
   * the full scatter. The pass still runs everywhere — the texture is the
   * point — it just stops being the same texture in all six countries. */
  const habit2 = HABIT[biomeName] || HABIT.heath;
  const boulders = habit2.clump < 0 ? rng.irange(0, 1) : rng.irange(1, 4);
  for (let i = 0, n = boulders; i < n; i++) {
    const cx = rng.irange(2, COLS - 3), cy = rng.irange(2, ROWS - 3);
    for (let y = cy; y < Math.min(cy + rng.irange(1, 2), ROWS - 1); y++)
      for (let x = cx; x < Math.min(cx + rng.irange(1, 3), COLS - 1); x++)
        if (t[y][x] === T.GROUND && rng.chance(0.8)) t[y][x] = T.ROCK;
  }

  // --- THICKETS AT THE WATERLINE -------------------------------------
  // The Silt Lung's second use — go under a hedge that is rooted in deep
  // water — was unreachable content for a whole round. Measured across 400
  // seeds: 12,218 thorn tiles in the game, ZERO of them with deep water
  // beside them, because thorn only ever existed as a border gate and the
  // apron pass scrubs every wet tile next to one. The technique had nowhere
  // to happen, and the check that claimed 200 of 200 wrote the water itself.
  //
  // So grow brambles along the shore. They are not gates and they seal
  // nothing: there is always a way round. What they do is give the Emberwake
  // a hedge to burn on every lake in the world, and give a player holding the
  // Silt Lung a shortcut the game never mentions.
  // The hedge goes in the SHALLOWS, not on the bank. Deep water is generated
  // in the band above the shallows (`wet > 0.715` inside `wet > 0.675`), so
  // every lake is ringed by wadeable water and dry land is NEVER orthogonally
  // adjacent to deep water — which is the real reason the technique had 0 of
  // 12,218 places to happen. A bramble standing in the shallows with the drop
  // at its foot is both the picture and the rule.
  const shore = [];
  for (let y = 2; y < ROWS - 2; y++) {
    for (let x = 2; x < COLS - 2; x++) {
      /* a shallow between deep water and dry ground -- or, on a coast the sea meets without a shallow (LS-385: the dust
       * islands), the dry tile itself beside the sea, so every shore grows its brambles */
      /* A LAKE'S HEDGE STANDS IN THE SHALLOWS; THE SEA'S STANDS BACK FROM THE WATER (LS-394, 2026-09-12; Vijay: "those
       * green assets along the water in particular look horrible"). The sea's brambles were rooted at the waterline,
       * stalks out of the surf; a coast's bramble is dune scrub now, on sand or ground one tile back from the shore --
       * a dry tile with no water beside it, beside a dry tile the sea touches. */
      const here = t[y][x];
      const ORTH = [[1, 0], [-1, 0], [0, 1], [0, -1]] as Array<[number, number]>;
      const isDry = (v) => v === T.GROUND || v === T.MOSS || v === T.SAND;
      const seaBeside = (x2, y2) => ORTH.some(([dx, dy]) => t[y2 + dy] && t[y2 + dy][x2 + dx] === T.SEA);
      if (here === T.SHALLOW) {
        let deep = false, dry = false;
        for (const [dx, dy] of ORTH) { const v = t[y + dy][x + dx]; if (v === T.WATER) deep = true; if (isDry(v)) dry = true; }
        // it has to be reachable to be worth anything, and there has to be a drop under it for the Lung to use
        if (deep && dry) shore.push([x, y]);
      } else if (here === T.GROUND || here === T.SAND) {
        let wet = false, back = false;
        for (const [dx, dy] of ORTH) {
          const v = t[y + dy][x + dx];
          if (v === T.SEA || v === T.WATER || v === T.SHALLOW) wet = true;
          else if (isDry(v) && seaBeside(x + dx, y + dy)) back = true;
        }
        if (!wet && back) shore.push([x, y]);
      }
    }
  }
  rng.shuffle(shore);
  for (let i = 0; i < shore.length && i < 7; i++) {   // seven: five gave 184 hedges in 40 worlds and six exactly 200, against a floor of MORE than 200, once the fill and the unbury pass took their share
    if (!rng.chance(0.75)) continue;
    const [x, y] = shore[i];
    t[y][x] = T.THORN;
    /* ...AND THE FIRST IS A CLUMP (LS-385): a sleeper beds only in shelter, three thorn tiles with sheltered neighbours,
     * and a shore of single brambles gave the beasts' islands nothing to bed a situation in. The first bramble of a
     * screen takes two dry neighbours with it. */
    if (i === 0) {
      // a block of four, not a line: shelter is a thorn with two sheltered neighbours, and a line of three has one such tile
      const dryAt = (nx, ny) => nx > 1 && ny > 1 && nx < COLS - 2 && ny < ROWS - 2 && (t[ny][nx] === T.GROUND || t[ny][nx] === T.MOSS || t[ny][nx] === T.SAND);
      for (const [sx2, sy2] of [[1, 1], [-1, 1], [1, -1], [-1, -1]]) {
        if (dryAt(x + sx2, y) && dryAt(x, y + sy2) && dryAt(x + sx2, y + sy2)) { t[y][x + sx2] = T.THORN; t[y + sy2][x] = T.THORN; t[y + sy2][x + sx2] = T.THORN; break; }
      }
    }
  }

  // TILES NO LATER PASS MAY TOUCH: the tier barrier and its jambs.
  //
  // Three separate passes ran after the border and quietly deleted them — the
  // hollow-entrance clearing writes a 5x4 block of GROUND, `carve` writes a
  // line of GROUND, and both did it wherever they liked. Measured: 65 of 3,512
  // gated edges (1.85%) could be walked through bare-handed and 28% of worlds
  // had at least one, plus 55 of 4,552 erased by the entrance clearing — a
  // doorframe with no door. `gate.mjs` cannot see it, because it only ever
  // proves that a route EXISTS; nothing in the project proved that a barrier
  // still stands.
  /* THE RAMPS ARE CUT, AND THEN THE CLIFFS ARE LOCKED.
   *
   * A cliff has to survive every later pass or it is not a cliff — measured
   * before this, only 66.5% of terrace feet reached the drawn map, because
   * `ensureConnected` carves a route wherever it needs one and the density
   * cull thinned the rest. What that leaves is a boundary broken every few
   * tiles, which at 1:1 reads as scattered dark blocks rather than as an edge.
   *
   * So the way between two terraces is a RAMP and there is exactly one per
   * pair of plateaus, chosen in `generateWorld` from the tiles where the two
   * actually touch. Every pair joins a region to one exactly a tier deeper —
   * the plateaus form a tree — so every ramp carries that tier's barrier, and
   * a cliff you cannot climb is the only thing holding the tiering up.
   * `ensureConnected` still runs and still has to succeed; it now has to do it
   * through the wood rather than through the rock. */
  /* A RAMP IS A CORRIDOR, NOT A HOLE IN A WALL.
   *
   * The first version cleared whatever cliff tiles fell in a 3x3 around the
   * two contact tiles, which opens the face and nothing else — so if the
   * ground immediately above the step was wood, or the ground below it was
   * water, the gap led nowhere. Measured: on two seeds of six the player
   * reached only tier 0 holding ALL THREE charms, because every way onward
   * was a gap with a tree behind it.
   *
   * So it is cut as a route: two tiles back on the low side, the step itself,
   * two tiles on the high side, and two tiles wide, with the barrier standing
   * in the step where the cliff was. Water is never cut — a channel through
   * the sea is not a ramp — and the barrier is locked so nothing fills it in.
   */
  /* A RAMP IS ONE FOOT TILE, OPENED.
   *
   * Regions are walkable ground now and the feet are the walls between them,
   * so a ramp needs no corridor: the two plateaus already reach the tile from
   * either side, and cutting it joins them. The barrier for the tier stands in
   * that tile, and it is locked so no later pass fills it in or clears it. */
  const rampAt = new Set();
  // strongest gate first, so a tile two ramps had to share keeps the harder one
  const ramping = [...(world.ramps || [])].sort((a, b) => a.tier - b.tier);
  for (const r of ramping) {
    const fill = T.RAMP;
    /* TWO TILES WIDE, because one is not a stair.
     *
     * Sixteen pixels cannot carry a flight of steps. Drawn boldly a one-tile
     * ramp reads as a barcode, and with jambs added to frame it, as a ladder;
     * drawn quietly enough to avoid both it disappears into the ground. Every
     * overworld in this idiom cuts its stairs two tiles across, and that is
     * the actual fix — the drawing was only ever the symptom.
     *
     * The second tile is taken along the CLIFF, perpendicular to the climb, so
     * the flight widens rather than lengthens; and only where that tile is
     * also rock, so a ramp never eats ground that was already open. */
    for (const [wx, wy] of rampTiles(r)) {
      const lx = wx - ox, ly = wy - oy;
      if (lx < 0 || ly < 0 || lx >= COLS || ly >= ROWS) continue;
      if (isWet(t[ly][lx])) continue;
      t[ly][lx] = fill;
      rampAt.add(lx + ',' + ly);
      locked.add(lx + ',' + ly);
    }
    // the approach: always the slope itself, never the barrier — a gated ramp
    // reads as a flight of steps with the rubble standing at the top of it
    if (r.down) {
      const lx = r.down[0] - ox, ly = r.down[1] - oy;
      if (lx >= 0 && ly >= 0 && lx < COLS && ly < ROWS && !isWet(t[ly][lx])
        && !locked.has(lx + ',' + ly)) {
        t[ly][lx] = T.RAMP;
        rampAt.add(lx + ',' + ly);
      }
    }
  }

  /* The trunk routes: wood standing on one comes down. Cliffs never do — the
   * routes were pathed inside a single terrace and cross between them only at
   * ramps, so nothing here can open a tier. */
  /* ...AND A RAMP IS APPROACHABLE FROM BOTH SIDES, OR IT IS NOT A RAMP.
   *
   * A ramp is sited on a cliff foot with a plateau either side, and `downTile`
   * clears one tile of ground on its low side -- looking SOUTH, because that
   * is where a north-facing wall's low ground is. A ramp cut through a wall
   * that drops EAST has its low ground to the east, which nothing clears, and
   * the wood pass then plants trees there like anywhere else. Traced on
   * contrast-0: the ramp at 48,12 exits onto a cliff and four trees, and the
   * 224-tile region behind it is unreachable holding every charm. Six of 44
   * ramps across four worlds had no open tile on their low side.
   *
   * So the tiles beside every ramp are routes too, and the sweep below lifts
   * what stands on them by the same rule: never a barrier, never a cliff,
   * never water -- only what nothing in the game could ever remove. */
  const beside = new Set();
  if (world.ramps) {
    for (const r of world.ramps) {
      for (const [wx, wy] of rampTiles(r)) {
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const lx = wx + dx - ox, ly = wy + dy - oy;
          if (lx >= 0 && ly >= 0 && lx < COLS && ly < ROWS) beside.add(lx + ',' + ly);
        }
      }
    }
  }
  // ...on every screen the seal touched, not only those with routes or ramps:
  // seed-37's one-tile islet was drowned eight rounds running and stayed open,
  // because its screen had neither and this sweep never ran there
  const sealed = (world.drown && world.drown.size) || (world.ford && world.ford.size) || (world.unseal && world.unseal.size) || (world.reef && world.reef.size);
  if (world.routes || beside.size || sealed) {
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        if (world.drown && world.drown.has((ox + x) + ',' + (oy + y))) {
          t[y][x] = T.SEA;                       // see the seal: sea the terrain forgot
          continue;
        }
        if (world.reef && world.reef.has((ox + x) + ',' + (oy + y))) {
          t[y][x] = T.ROCK; locked.add(x + ',' + y);   // see the seal: a boulder reef where the tide would land on higher ground
          continue;
        }
        const unseal = world.unseal && world.unseal.has((ox + x) + ',' + (oy + y));
        if (world.ford && world.ford.has((ox + x) + ',' + (oy + y)) && !unseal) {
          t[y][x] = T.SHALLOW; locked.add(x + ',' + y);   // see the seal: a ford to an islet -- unless a later round cut a sand bar over it
          continue;
        }
        const onRoute = world.routes && world.routes.has((ox + x) + ',' + (oy + y));
        if (!onRoute && !beside.has(x + ',' + y) && !unseal) continue;
        const v = t[y][x];
        if (unseal && v === T.SEA) { locked.add(x + ',' + y); continue; }   // THE SEA IS NOT BRIDGED (LS-383): a strait is crossed by the water's own ways, never by a bar the cutter lays
        if (unseal && isWet(v)) { t[y][x] = T.SAND; locked.add(x + ',' + y); continue; }   // a sand bar across a river
        if (unseal && (v === T.TREE || v === T.ROCK || v === T.CLIFF)) {
          // the seal names only trees, boulders and a lump's skirt, and has
          // cleared the skirt's terrain face; a lock here is a tier lock, and
          // none of the three is one
          // ...and locked: a stone stood on the very tile the seal cut to free
          // the spawn (seed-122, Hollow I fenced in)
          t[y][x] = T.GROUND; locked.add(x + ',' + y); continue;
        }
        if (locked.has(x + ',' + y)) continue;
        /* WHERE A TRUNK ROUTE CROSSES A RIVER, THERE IS A FORD.
         *
         * A river is `SHALLOW`, which the tide opens — so a route that had to
         * cross one was cut for a player who does not hold the tide yet, and
         * the hollow on the far side was unreachable. Measured on seed-4, 23
         * of 1,009 route tiles were impassable bare-handed and every one of
         * them was river.
         *
         * The route already prefers dry ground and only crosses where there is
         * no way round, so the crossings are few and each is a real place: a
         * bar of sand through the shallows, which is what a ford looks like.
         * The rest of the river is untouched and the tide still owns it. */
        if (terr.river[terr.at(ox + x, oy + y)] && v === T.SHALLOW) {
          if (tierLine(x, y)) continue;
          t[y][x] = T.SAND;
        } else if (isSolid(v) && !isWet(v)) t[y][x] = T.GROUND;
      }
    }
  }

  /* --- THERE IS NO BORDER. The ground keeps going.
   *
   * This wrote a ring of wall around every screen and then punched holes in
   * it, and it is the single thing that made the world a lattice. The ring is
   * 52 of a screen's 192 tiles, so it was repainting 27% of the world AFTER
   * the terrain pass had decided what was there — measured, the sea covers
   * 17.8% of the map and only 6.0% of it survived to be drawn, because the
   * ocean lies along the map's rim, which is exactly where this ran.
   *
   * What held gating up was never the wall itself but the SCREEN-PAIR GRAPH
   * it made meaningful. That graph cannot survive contact with terrain —
   * derive its links from the ground and 106 of 112 adjacent pairs are joined,
   * since one walkable tile anywhere along a seam joins them. Gating moves to
   * the region tree instead, where the plateaus are separated by cliffs and
   * every ramp between two of them is a bridge. */

  const openings = [];      // reachable with no items
  /** Every tile a border opening owns. The treeline below thickens inward and
   *  must not close over any of them. */
  const doorway = new Set();
  /* THE WIDENED SEAM KEEPS ITS OWN SET, and this is not tidiness.
   *
   * `doorway` means "a tile the door owns": the treeline must not close over
   * it, AND the spoil heap, the landmarks and the scatter passes all skip it
   * so nothing is piled in a doorway. The first version put every widened
   * seam tile in there, which is true of the first meaning and false of the
   * second — a seam is now a dozen tiles wide, so a hollow mouth near a
   * screen edge had every candidate spot for its heap excluded and laid
   * NOTHING. `every hollow mouth has spoil outside it` caught it at heap 0
   * against a want of 8.
   *
   * So the treeline reads both sets and everything else reads `doorway`. */
  const seamWide = new Set();
  for (const e of EDGES) {
    const [dx, dy] = e.d;
    if (!linked(world, sx, sy, dx, dy)) continue;
    /* A SEA EDGE IS NOT A DOORWAY (LS-383). The links are a maze over every screen, sea included; paving a seam that
     * lies in open water laid the shallow marks along the coasts and gave the cutter a target it could never reach,
     * so on some islands it spent its rounds on the sea and left the glade walled in. */
    if (e.tiles().every(([x, y]) => t[y][x] === T.SEA || t[y][x] === T.WATER)) continue;

    // The seam itself is always open. The barrier stands one tile inboard,
    // with stone jambs either side so the mouth behind it is reachable only
    // through it — or over it.
    /* ...BUT NOT THROUGH A CLIFF.
     *
     * This opened a lane of GROUND across every linked screen edge, which was
     * the door in the border wall. The wall is gone and the lane is not, so it
     * was cutting a hole through whatever terrain happened to lie on a screen
     * boundary — measured, 165 of 1,661 terrace steps in the world were
     * walkable, almost all of them GROUND against GROUND, and all of them on a
     * seam. A staircase every screen edge, placed by the chunk grid.
     *
     * `locked` holds the cliffs by the time this runs, so the lane goes round
     * them the way it already goes round a tier barrier. */
    // an opening across a river is a sand ford, the same as an approach
    // (see `inner()` below): paved to ground it read as a hole in the river.
    // BUT NEVER ACROSS A TIER LINE: a river parts two regions, and where they
    // differ in tier the water is the tide's gate; a ford there walked a
    // tier-1 bank bare-handed (seed-173, seed-296). Such a tile stays as it is.
    const pave = (x, y) => tierLine(x, y) ? t[y][x]
      : (t[y][x] === T.SHALLOW && terr.river[terr.at(ox + x, oy + y)]) ? T.SAND : T.GROUND;
    for (const [x, y] of e.tiles()) {
      if (locked.has(x + ',' + y)) continue;
      t[y][x] = pave(x, y); doorway.add(x + ',' + y);
    }
    for (const [x, y] of e.mouth()) {
      if (locked.has(x + ',' + y)) continue;
      t[y][x] = pave(x, y); doorway.add(x + ',' + y);
    }
    for (const [x, y] of e.gate()) {
      if (locked.has(x + ',' + y)) continue;
      t[y][x] = T.GROUND;
    }
    /* AN UNGATED SEAM IS NOT A DOOR. IT IS WHERE THE GROUND KEEPS GOING.
     *
     * Every screen was walled on all four sides and then punched with a hole
     * two tiles wide at the exact centre of the edge — `OPEN_X = [7, 8]`,
     * `OPEN_Y = [5, 6]`, the same two tiles on every screen in every world.
     * Measured over 320 screens: the outer ring is 91.4% solid and 320 of 320
     * screens are framed on all four sides, against an interior that is 34.5%
     * solid. That is the walled-rectangle finding an art lens has ranked first
     * for three rounds, and it is why the world reads as a corridor of boxes.
     *
     * Most of those walls are not holding anything up. Of 896 adjacent screen
     * joins over 8 seeds: 35.2% are not linked at all and are honest terrain
     * boundaries, 13.4% carry a tier barrier and NEED the chokepoint — a
     * barrier in a twelve-tile gap is not a barrier — and **51.5% are linked
     * with no gate whatsoever**. Half of every boundary in the game was a wall
     * with a pointless hole in it.
     *
     * So an ungated seam opens along its whole length, ragged, by the same
     * world-space noise that makes the woods and lakes continuous.
     *
     * SAMPLED AT THE SEAM, NOT AT THE ROW. Both screens either side must open
     * the SAME tiles or one of them draws a wall where its neighbour draws
     * grass. My north edge is world row `sy * ROWS`; the neighbour's south
     * edge is the same seam approached from the other side, so both sample
     * that one shared coordinate rather than their own `oy + y`, which differ
     * by a row and would disagree.
     *
     * `locked` is respected so a barrier's jambs and stones survive, and every
     * opened tile joins `doorway` so the treeline pass below cannot close back
     * over it. `ensureConnected` still runs afterwards and `test/gate.ts`
     * still has to prove the gating over 300 seeds — widening an UNGATED seam
     * cannot create a tier crossing, which is why this is safe to do and why
     * the gated 13.4% keeps its two-tile lane. */
    const seamAnchors: any[] = [];

      const wideAnchors = seamAnchors;
      const along = dy !== 0;
      const seamX = (dx < 0 ? sx : sx + 1) * COLS;
      const seamY = (dy < 0 ? sy : sy + 1) * ROWS;
      const ex = dx < 0 ? 0 : COLS - 1, ey = dy < 0 ? 0 : ROWS - 1;
      for (let i = 1; i < (along ? COLS : ROWS) - 1; i++) {
        const x = along ? i : ex, y = along ? ey : i;
        if (locked.has(x + ',' + y)) continue;
        const n = along ? fbm(world.seedNum + 907, ox + x, seamY, 0.17)
          : fbm(world.seedNum + 907, seamX, oy + y, 0.17);
        if (n < 0.55) continue;              // the treeline still closes here
        /* AND IT HAS TO LEAD SOMEWHERE.
         *
         * The first version opened the seam tile and one tile inboard, which
         * on a screen whose second ring is treeline opens a POCKET: a gap you
         * can see through into solid rock. The suite caught all of it —
         * `every stone that was kept leaves the screen open` reported ways in
         * cut off from the rest of the screen, `the player can always stand
         * where the marks can be read` lost a stone, and `a body sealed
         * against a border is told so` refused 1.74% of border tiles.
         *
         * So the lane is cut inward until it reaches ground that already
         * exists, up to the depth of the treeline itself. Bounded, because an
         * unbounded carve is a bulldozer: past the third ring the screen's own
         * terrain is the thing worth keeping. If the lane cannot reach open
         * ground in that distance the seam tile is left walled, which is the
         * honest answer — that neighbour is reached another way. */
        /* THE DECISION IS THE SEAM'S; THE LANE IS EACH SCREEN'S OWN.
         *
         * A first version bailed out of opening when it could not find ground
         * within four tiles. That reads as prudent and it silently broke the
         * property this whole pass depends on: the test is each screen's OWN
         * interior, which differs across the join, so one side opened where
         * the other walled. Seam agreement fell 100% -> 95.6%, which on the
         * glass is grass meeting a wall at the same tile of the same seam.
         *
         * So whether a seam tile opens is decided ONLY by the shared noise
         * above, and the lane inward is then cut unconditionally until it
         * meets ground. Beyond the treeline the interior is 34% solid, so
         * five tiles reaches open ground nearly always, and where it does not
         * the result is a deep nook rather than a sealed pocket. */
        /* CUT STONE, NEVER FEATURES. This wrote GROUND over the seam tile
         * unconditionally, and a hollow's spoil heap and the worn path down
         * from its door both reach the screen edge — so the pass that opens
         * the world quietly deleted the evidence that anybody dug it. The
         * suite named it: `every hollow mouth has spoil outside it` and `and
         * the heap is as big as the tunnel behind it`. A tile that is already
         * something is already open; only rock becomes ground. */
        // a river under a widened seam is forded on sand, not paved (LS-161)
        const paveWide = (px, py) => tierLine(px, py) ? t[py][px] : (t[py][px] === T.SHALLOW && terr.river[terr.at(ox + px, oy + py)]) ? T.SAND : T.GROUND;
        if (!locked.has(x + ',' + y) && isSolid(t[y][x])) t[y][x] = paveWide(x, y);
        seamWide.add(x + ',' + y);
        let lx = x, ly = y;
        for (let step = 1, cx = x, cy = y; step <= 5; step++) {
          cx -= dx; cy -= dy;
          if (cx <= 0 || cy <= 0 || cx >= COLS - 1 || cy >= ROWS - 1) break;
          if (locked.has(cx + ',' + cy)) break;
          lx = cx; ly = cy;
          if (!isSolid(t[cy][cx])) break;         // it already leads somewhere
          t[cy][cx] = paveWide(cx, cy); seamWide.add(cx + ',' + cy);
        }
        /* ...AND THE CONNECTIVITY PASS HAS TO KNOW IT EXISTS.
         *
         * `ensureConnected` is given `openings`, which holds ONE anchor per
         * edge — `e.anchor`, a point inside the old two-tile door. A tile
         * opened eight columns away from that door is not a target, so
         * nothing proved it reached the rest of the screen, and the suite said
         * so precisely: `every stone that was kept leaves the screen open`
         * reported 10 of 11 ways in cut off from the rest. Each lane's inner
         * end joins the anchor list, so the same flood that has always proved
         * a screen crossable now has to prove these too, and carves only where
         * one is genuinely stranded. */
        wideAnchors.push([lx, ly]);
      }
    
    /* A STONE BESIDE THE BARRIER, NAMING WHAT OPENS IT.
     *
     * Which charm opens which tier is a per-seed permutation (`world.order`),
     * and until now the only way to learn it was to walk into a barrier and
     * find out. That is the game teaching by refusal, which is the same
     * failure as a caption: it tells you after the fact and it tells you
     * nothing about the world.
     *
     * Whoever cut these barriers wrote on them — which is true of the ones
     * that were cut. Measured across 150 seeds, **64.4% of barrier stones
     * stand beside something nobody cut**: WATER 32.7% and THORN 31.7%
     * against RUBBLE's 31.4%. The fiction the mark carries is therefore
     * narrower than "these people built this": they marked what stopped them,
     * and a lake stopped them as surely as a wall did.
     *
     * The stone stands one tile
     * INBOARD of the gate and off to the side of the lane, so it never blocks
     * the way through — it is beside the door, not in it — and it bears the
     * deepmark for the charm this barrier answers to. A player who has learned
     * two marks can read a wall before they touch it; a player who has not
     * sees a carved stone, which is the invitation to learn them.
     *
     * `locked` so no later pass writes over it: `carve` and the entrance
     * clearing both scribble GROUND wherever they like, and this is exactly
     * the kind of thing they have erased before.
     */
    /* The vestibule behind a doorway — and it stops at a cliff like everything
     * else. Unguarded, this was the largest hole in the terrace walls: the
     * leaks measured 72 per world and every one of them was in a screen's
     * INTERIOR, not on its edge, which is exactly where `inner()` writes. */
    for (const [x, y] of e.inner()) {
      if (locked.has(x + ',' + y)) continue;
      // a river under the approach is crossed on a sand ford, as a route
      // crosses one, not paved over: the gap read as a hole in the river
      if (isWet(t[y][x]) && tierLine(x, y)) continue;   // the tide's gate, not a hole
      if (t[y][x] === T.SHALLOW && terr.river[terr.at(ox + x, oy + y)]) { t[y][x] = T.SAND; doorway.add(x + ',' + y); continue; }
      if (isSolid(t[y][x]) || t[y][x] === T.SHALLOW) t[y][x] = T.GROUND;
      doorway.add(x + ',' + y);
    }
    for (const [x, y] of e.gate()) doorway.add(x + ',' + y);
    openings.push(e.anchor.slice());

  }

  /* --- AND NO TREELINE EITHER, for the same reason.
   *
   * This thickened rings 1 and 2 of every screen so the border would not read
   * as a drawn rectangle. With the border gone it has nothing to soften and
   * would simply BE the rectangle — a ragged one, which is worse, because a
   * ragged frame still reads as a frame and cannot be measured as one. The
   * woods come from world-space noise and cross seams already.
   */
  // The spawn clearing must itself be a connectivity target. Without this the
  // player can be walled into a pocket by the terrain noise — and with only
  // one opening on most spawn screens, ensureConnected would no-op entirely.
  let spawnAnchor = null;
  if (sx === world.start.x && sy === world.start.y) {
    // A composed clearing: a soft-edged glade with standing stones off to one
    // side, rather than a 64px rectangle of flat ground stamped into a field.
    const [spx, spy] = spawnTile(world.seedNum);
    const cx = spx + 0.5, cy = spy + 0.5;
    for (let y = 1; y < ROWS - 1; y++) {
      for (let x = 1; x < COLS - 1; x++) {
        const d = Math.hypot(x - cx, (y - cy) * 1.25) + rng.range(-0.6, 0.6);
        if (locked.has(x + ',' + y)) continue;   // not through the cliff either
        if (d < 2.4) t[y][x] = T.GROUND;
        else if (d < 4.2) t[y][x] = rng.chance(0.62) ? T.MOSS : T.GROUND;
        else if (d < 5.4 && isSolid(t[y][x]) && rng.chance(0.5)) t[y][x] = T.MOSS;
      }
    }
    /* A STANDING STONE HERE COSTS RUNS, MEASURED (2026-09-08, LS-295). The comment above promises "a
     * glade with standing stones off to one side" and there is none: stones are made at barriers and
     * gated ramps, so whether one lands near where you wake is luck -- one within a screen of the
     * start in 17 worlds of 24, none in seven. I set one here, three tiles off the spawn and locked so
     * later passes route around it. It worked: 17/24 became 24/24, 23 of them on the start screen.
     *
     * AND IT MADE THE GAME HARDER. Driven with `quest.ts`'s own runner over six seeds, both ways: FOUR
     * of six finished without the stone and TWO with it. q0 went from won in 16 deaths to lost at 24;
     * q6 from won to lost. Nothing flipped the other way. A solid tile locked inside the glade changes
     * what `ensureConnected` can carve and so the ways out of the first screen, which is a real cost
     * for a cosmetic gain. Reverted. If it is tried again, the thing to beat is that measurement:
     * `test/_probe_questarm.ts` runs the check's own runner in both arms. */
    spawnAnchor = [spx, spy];
  }

  // --- connectivity. Pass 1 proves the screen is crossable with no items at
  // all; pass 2 proves the gated exits are reachable once you hold the item.
  const carved = new Set();
  const anchors = spawnAnchor ? [spawnAnchor] : [];
  /* THE ANCHORS THAT ARE ONE SCREEN. An exit on the upper plateau and one on
   * the lower are joined by a stair on some other screen, not this one, so
   * asking every later pass to keep them connected refused every candidate:
   * 12 of 40 abandoned shafts were never dug, every one of 30 spots tried.
   * Keep the largest group that is connected with every charm in hand. */
  const free = anchors.concat(openings);
  ensureConnected(t, free, carved, REACH.NONE, world.order, locked);
  ensureConnected(t, free, carved, REACH.PODS, world.order, locked);
  /* WHAT IS JOINED NOW STAYS JOINED. Every later pass asks `stillConnected`
   * before it stands something on a tile. Asked bare-handed it refused every
   * candidate on a screen whose openings only meet by a stair elsewhere; asked
   * with every charm it let a stone cut the only bare-handed path (seed-122,
   * Hollow I fenced in). So the anchors carry a baseline: for each reach, the
   * set joined to the first anchor as the screen stands after carving, and a
   * candidate must keep exactly those joined. */
  withBaseline(t, free, world.order);
  ensureConnected(t, withBaseline(t, free, world.order), carved, REACH.ALL, world.order, locked);
  joinSeams(t, locked, seamWide);

  // --- THE OPENING SCREEN'S ONE PIECE OF TEACHING, PLACED LAST AND OUT OF
  // STONE THAT IS ALREADY THERE.
  //
  // One boulder has come apart, its pieces around it: the first screen shows
  // what happens to stone here without saying it, and GOAL.md scores saying
  // it negatively. The stone it came off is RUBBLE, not ROCK, so a player who
  // reads the cue and tries a pod on the thing teaching it gets something
  // back.
  //
  // It used to be stamped in before the connectivity passes, at a fixed
  // offset from a fixed anchor. Once the anchor moved per seed that broke
  // twice over: the six solid tiles landed on the hollow mouth and erased it
  // (3 of 3,000 seeds lost `Hollow I` entirely), and where they landed on a
  // route `ensureConnected` carved the RUBBLE away again, leaving 13 of 120
  // spawn screens with the debris but nothing blastable — the exact defect
  // the RUBBLE was introduced to fix.
  //
  // Built here instead, after every connectivity proof, and only ever by
  // LOOSENING: the boulder is stone that is already standing, RUBBLE and
  // DEBRIS are both easier to get past than what they replace, and no tile
  // that was passable becomes solid. It cannot change any answer above it.
  if (spawnAnchor) {
    const [ax, ay] = spawnAnchor;
    let best = null, bestD = Infinity;
    for (let y = 1; y < ROWS - 1; y++) {
      for (let x = 1; x < COLS - 1; x++) {
        // any standing stone or trunk, but never a border tile: the tier
        // gates live on the border and are barriers of exactly these kinds
        if (x < 2 || y < 2 || x > COLS - 3 || y > ROWS - 3) continue;
        if (t[y][x] !== T.ROCK && t[y][x] !== T.TREE && t[y][x] !== T.RUBBLE) continue;
        // near enough to read as part of the glade, not so near it is in it
        const d = Math.hypot(x - ax, y - ay);
        if (d < 2.2) continue;
        // it has to have somewhere for its pieces to lie
        let open = 0;
        for (const [dx, dy] of ([[1, 0], [-1, 0], [0, 1], [0, -1]]) as Array<[number, number]>) {
          const nx = x + dx, ny = y + dy;
          if (nx < 1 || ny < 1 || nx >= COLS - 1 || ny >= ROWS - 1) continue;
          if (!isSolid(t[ny][nx])) open++;
        }
        if (open < 1) continue;
        if (d < bestD) { bestD = d; best = [x, y]; }
      }
    }
    // ...AND IF THERE IS NO STONE LEFT TO USE, RAISE ONE — BUT PROVE IT
    // FIRST. 12 of 120 spawn screens have no standing stone inside the glade
    // ring at all, and dropping the cue on those would trade a 100%
    // onboarding guarantee for start-tile variety. A new boulder is the one
    // write here that TIGHTENS the screen, so it is provisional: lay it, run
    // the same bare-handed flood `ensureConnected` just ran, and take it back
    // if anything that was joined stopped being joined.
    if (!best) {
      const joined = () => {
        if (free.length < 2) return true;
        const seen = flood(t, free[0], REACH.NONE, world.order);
        return free.every(([fx, fy]) => reached(seen, fx, fy));
      };
      const before = joined();
      for (let r = 3; r <= 6 && !best; r++) {
        for (let y = 2; y < ROWS - 2 && !best; y++) {
          for (let x = 2; x < COLS - 2; x++) {
            if (isSolid(t[y][x])) continue;
            if (Math.round(Math.hypot(x - ax, y - ay)) !== r) continue;
            const was = t[y][x];
            t[y][x] = T.ROCK;
            if (!before || joined()) { best = [x, y]; break; }
            t[y][x] = was;
          }
        }
      }
    }
    if (best) {
      const [bx, by] = best;
      t[by][bx] = T.RUBBLE;
      // THE PIECES FIRST, THE SCORCH ONLY IF THERE IS ROOM LEFT. Laying the
      // scorch first spent the boulder's one open neighbour on it, and the
      // cue is the RUBBLE-beside-DEBRIS pair — a lone boulder teaches nothing.
      // 1 of 600 spawn screens came out that way.
      const spots = [];
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1],
        [1, 1], [-1, 1], [1, -1], [-1, -1], [2, 0], [-2, 0], [0, 2], [0, -2]]) {
        const nx = bx + dx, ny = by + dy;
        if (nx < 1 || ny < 1 || nx >= COLS - 1 || ny >= ROWS - 1) continue;
        if (isSolid(t[ny][nx])) continue;
        spots.push([nx, ny]);
      }
      for (let k = 0; k < spots.length && k < 2; k++) t[spots[k][1]][spots[k][0]] = T.DEBRIS;
      if (spots.length > 2) t[spots[2][1]][spots[2][0]] = T.SCORCH;
    }
  }

  // --- secret: hide it behind blastable rubble. Prefer converting a boulder
  // that is already there; otherwise drop a new one somewhere that provably
  // does not cut the screen in half.
  /* NO SCREEN IS A FLAT FIELD. The habit pass only ever thins; a screen the
   * terrain left with a boulder or two and no water stayed that way (blank-0
   * 2,5: 6% terrain, nothing to walk around). If under 15% of the screen is
   * terrain and it is mostly ground, boulders and trees are planted on open
   * interior tiles, clumped against what is there, until it is. Laid late,
   * after the seal's cuts, the route paving and the beside-the-ramp sweep,
   * which between them took 38 planted tiles down to 11 on blank-0 2,5. */
  {
    const TERR = new Set([T.TREE, T.ROCK, T.THORN, T.CLIFF, T.RAMP]);   // ...and whatever is wet (isWet, spelled once)
    let terrain = 0, ground = 0, sea = 0;
    for (const row of t) for (const v of row) { if (TERR.has(v) || (isWet(v) && v !== T.SEA)) terrain++; if (v === T.GROUND || v === T.MOSS || v === T.SAND) ground++; if (v === T.SEA) sea++; }
    // measured over LAND: a coast screen is half sea, and the sea is not flat ground
    const total = Math.max(1, COLS * ROWS - sea);
    (world.fillWhy ||= new Map()).set(sx + ',' + sy, { terrain, ground, total });
    if (terrain / total < 0.15 && ground / total >= 0.2) {
      const wantTerrain = Math.ceil(total * 0.2);   // a fifth: past the 15% the suite asks, so rounding never lands on it
      const [fspx, fspy] = sx === world.start.x && sy === world.start.y ? spawnTile(world.seedNum) : [-9, -9];   // the glade round the spawn tile stays open
      const spots = [];
      for (let y = 1; y < ROWS - 1; y++) for (let x = 1; x < COLS - 1; x++) {   // one tile in: a coast strip three rows deep has no interior otherwise
        if (t[y][x] !== T.GROUND && t[y][x] !== T.MOSS && t[y][x] !== T.SAND) continue;   // a boulder sits on a beach too
        if (cliffAt.has(x + ',' + y)) continue;
        if (world.routes && world.routes.has((ox + x) + ',' + (oy + y))) continue;
        if (locked.has(x + ',' + y) || doorway.has(x + ',' + y)) continue;
        if (Math.abs(x - fspx) <= 1 && Math.abs(y - fspy) <= 1) continue;   // 22 of 400 spawn glades had a planted neighbour
        // ...and never against a standing stone: the wood closed over 7 of
        // 2199 stones, which must keep open ground on a side to be read
        if (NEIGHBOURS.some(([dx, dy]) => t[y + dy] && t[y + dy][x + dx] === T.STONE)) continue;
        // ...nor on a gate's apron: the cue pass lays debris on the open
        // tile at a rubble run's end, after this, and a tree planted there
        // first left the start screen of soak-spawncue-562 with stone that
        // showed nothing of what stone does
        if (NEIGHBOURS.some(([dx, dy]) => { const u = t[y + dy] && t[y + dy][x + dx]; return u === T.RUBBLE || u === T.CRACK || u === T.THORN; })) continue;
        let near = 0;
        for (const [dx, dy] of NEIGHBOURS) if (t[y + dy] && (t[y + dy][x + dx] === T.TREE || t[y + dy][x + dx] === T.ROCK)) near++;
        spots.push([x, y, near * 0.6 + rng.range(0, 1)]);
      }
      spots.sort((a, b) => b[2] - a[2]);
      const kind = biomeName === 'scree' || biomeName === 'ash' || biomeName === 'dust' ? T.ROCK : T.TREE;
      /* ...AND NEVER ACROSS A WAY. This pass had no connectivity guard: it
       * kept off routes, locked tiles and doorways and trusted that to be
       * enough, and a change to which spots it skipped (the spawn glade)
       * moved a tree onto the one tile seed-38's second hollow was reached
       * through. Every planting is checked against the screen's anchors the
       * way a stone is, and put back if it cuts anything off. */
      const anchorsF = withBaseline(t, free, world.order);
      for (let i = 0; i < spots.length && terrain < wantTerrain; i++) {
        const [x, y] = spots[i];
        const was = t[y][x];
        t[y][x] = rng.chance(0.25) ? (kind === T.ROCK ? T.TREE : T.ROCK) : kind;
        if (!stillConnected(t, anchorsF)) { t[y][x] = was; continue; }
        terrain++;
      }
    }
  }
  /* THE CUE AT THE FOOT, laid after the beside-the-ramp sweep (which
   * flattens wood and rock to ground and took the cue with it). A rubble
   * gate spills debris and a thorn gate scorches the ground before it, the
   * way every breakable in the wilds carries a cue at its foot; a stair's
   * cells are walled in, so the open tile at each end of the run takes it. */
  // WHAT THE GROUND SAYS. This game teaches its verbs with the world, not with
  // text, and two of the three verbs had nothing to read: only 35.5% of
  // overworld cracked stone had a fleck of debris at its foot, and NO thorn
  // above ground grew out of scorched earth — the exact cue the hollows use
  // for the same hedge. Last, so the barriers are already standing.
  for (let y = 1; y < ROWS - 1; y++) {
    for (let x = 1; x < COLS - 1; x++) {
      const v = t[y][x];
      const tell = v === T.RUBBLE ? T.DEBRIS : v === T.THORN ? T.SCORCH : null;
      if (!tell) continue;
      let already = false;
      const open = [];
      for (const [dx, dy] of DIRS) {
        const nx = x + dx, ny = y + dy;
        if (nx < 1 || ny < 1 || nx >= COLS - 1 || ny >= ROWS - 1) continue;
        if (t[ny][nx] === tell) { already = true; break; }
        // never the barrier or its jambs, and never a tile that is doing a job
        if (locked.has(nx + ',' + ny)) continue;
        if (t[ny][nx] === T.GROUND || t[ny][nx] === T.MOSS) open.push([nx, ny]);
      }
      if (already || !open.length) continue;
      const [nx, ny] = open[mix(world.seedNum, sx, sy, x * 31 + y) % open.length];
      t[ny][nx] = tell;
    }
  }

  // ...AND WHAT WENT OVER IT. Nothing in the world said the Rattle can carry
  // you: standing beside your own blast pitches you three tiles, which is the
  // one technique that crosses a barrier you have no charm for, and a player
  // could finish the game without ever suspecting it. No text — a scuff. A
  // gouge on your side of a tier barrier and a matching one in the mouth
  // beyond it: two marks in line across something nothing walks through.
  /* A WAYSHRINE ON A SCREEN THAT HOLDS NOTHING ELSE.
   *
   * Two lenses, five rounds, the same finding from opposite ends: 42.3% of
   * wild screens carry nothing a player can act on, and five interactive tile
   * kinds — PUSH, BRAZIER, PLATE, CRACK, IDOL — exist and appear on ZERO of
   * 2,560 wild screens. The biome grammar bought texture; texture is not
   * something to do.
   *
   * The verb was already built: `entities.js` lights a brazier with the
   * Emberwake, and `game.lightBrazier` now charts the ring of screens around
   * it. So this places the thing, and only where the screen would otherwise
   * be empty — no mouth, no cache, no standing stone. A screen that already
   * has a reason to stop does not need another.
   *
   * Provisional, like every other placement here that can tighten a screen:
   * a brazier is SOLID, so it is laid, the screen is flooded bare-handed, and
   * it is taken back if anything that was joined stopped being joined. The
   * stone rule two blocks down learned that the hard way at 3,000 seeds.
   */
  /* ...AND NEVER IN THE OPENING GLADE.
   *
   * The start screen is exactly the kind of open ground this looks for, so
   * the first version put a shrine in it — and a shrine is SOLID. Three
   * checks went red at once: the spawn tile stopped being standable on 17 of
   * 400 worlds, and the clear ring the glade is composed around was broken on
   * 99. The opening glade is the most carefully arranged screen in the game
   * and it is not a screen with nothing on it. */
  /* A STONE AT THE FOOT OF EVERY GATED STAIR. The barrier stones used to
   * stand beside a seam gate and name the charm that opens it; the gates
   * moved into the corridor runs (one stair per ~20 tiles of shared wall,
   * gated by the tier of every region beside it), and no stone followed --
   * in 40 of 40 worlds no stone named hollow I or II, and a bought map was
   * the only way to learn where a charm is spent. Each run with a tier gets a
   * two-tile stone within three tiles of its foot, on open ground off the
   * routes, saying the charm the stair asks for. */
  const firstOfTier = new Map();   // the first two stairs of each tier always try (one may have no room)
  for (const r of world.ramps || []) if (r.tier && (firstOfTier.get(r.tier) || []).length < 2) firstOfTier.set(r.tier, (firstOfTier.get(r.tier) || []).concat([r]));
  for (const r of world.ramps || []) {
    if (!r.tier || !world.order[r.tier - 1]) continue;
    // the first stair of each tier always carries its stone; the rest one in
    // four, so a world reads a handful of markers, not a stone on every third
    // screen (~23 pairs a world when every gated stair had one)
    if (!firstOfTier.get(r.tier).includes(r) && (mix(world.seedNum, r.x * 7 + r.y * 131, r.tier * 17 + 5) & 3) !== 0) continue;
    const foot = r.down || [r.x2, r.y2];
    const fx = foot[0] - ox, fy = foot[1] - oy;
    // a foot on the seam still gets its stone on this side: the candidates
    // are bounded below, the foot itself need not be inboard (read-17's two
    // guaranteed stairs of a tier both stood at local x 0 and were skipped)
    if (fx < 0 || fy < 0 || fx >= COLS || fy >= ROWS) continue;
    const says = world.order[r.tier - 1];
    // one gate stone a screen: three pairs on one screen read as a pattern,
    // not a marker (with one a charm a screen a world carried ~25 pairs)
    if (world.stones && [...world.stones].some(([k]) => k.startsWith(sx + ',' + sy + '|'))) continue;
    const gateCellsHere = new Set(rampTiles(r).map(([gx, gy]) => (gx - ox) + ',' + (gy - oy)));
    const anchorsG = withBaseline(t, free, world.order);
    const openG = (v) => v === T.GROUND || v === T.MOSS || v === T.SAND;
    const onRouteG = (x, y) => !!(world.routes && world.routes.has((ox + x) + ',' + (oy + y)));
    // ...and never in the spawn glade: a gate stone stood beside the spawn
    // tile on 22 of 400 start screens once nearly every stair got one
    const [gspx, gspy] = sx === world.start.x && sy === world.start.y ? spawnTile(world.seedNum) : [-9, -9];
    const okCell = (x, y, routesToo) => x >= 1 && y >= 1 && x < COLS - 1 && y < ROWS - 1
      && !locked.has(x + ',' + y) && !doorway.has(x + ',' + y) && !gateCellsHere.has(x + ',' + y)
      && (routesToo || !onRouteG(x, y)) && openG(t[y][x])
      && !(Math.abs(x - gspx) <= 1 && Math.abs(y - gspy) <= 1) && !(Math.abs(x - gspx) <= 1 && Math.abs(y - 1 - gspy) <= 1);
    let placed = false, triedG = 0;
    // never on a worn route -- the gate suite proves every trunk route open,
    // and a stone on one blocked 261 of 301 seeds -- so look further instead
    for (const routesToo of [false]) for (let rad = 1; rad <= 5 && !placed; rad++) {
      for (let dy = -rad; dy <= rad && !placed; dy++) {
        for (let dx = -rad; dx <= rad && !placed; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== rad) continue;
          const bx = fx + dx, by = fy + dy;
          if (!okCell(bx, by, routesToo) || !okCell(bx, by - 1, routesToo)) continue;
          // and a way to stand before it: an open tile beside the lower stone
          // (21 of 2295 stones were boxed in by trees, cliff and water)
          if (![[1, 0], [-1, 0], [0, 1]].some(([ex, ey]) => bx + ex >= 0 && by + ey >= 0 && bx + ex < COLS && by + ey < ROWS && openG(t[by + ey][bx + ex]))) continue;
          triedG++;
          const wasA = t[by][bx], wasB = t[by - 1][bx];
          t[by][bx] = T.STONE; t[by - 1][bx] = T.STONE;
          if (stillConnected(t, anchorsG)) {
            const sm = (world.stones ||= new Map());
            // the one stone a world that says rako keeps saying it when its screen is made (the lore lens, round 39: generation overwrote it on four seeds in seven)
            const k1 = sx + ',' + sy + '|' + bx + ',' + by, k2 = sx + ',' + sy + '|' + bx + ',' + (by - 1);
            sm.set(k1, world.rakoKey === k1 ? 'unknown' : says);
            sm.set(k2, world.rakoKey === k2 ? 'unknown' : says);
            locked.add(bx + ',' + by); locked.add(bx + ',' + (by - 1));
            placed = true;
          } else { t[by][bx] = wasA; t[by - 1][bx] = wasB; }
        }
      }
    }
    (world.stoneWhy ||= []).push({ sx, sy, foot: [fx, fy], says, tried: triedG, placed, anchors: anchorsG.length });
  }
  const isStart = sx === world.start.x && sy === world.start.y;
  if (!isStart) {
    const anchors2 = withBaseline(t, free, world.order);
    const cand = [];
    for (let y = 3; y < ROWS - 3; y++) {
      for (let x = 3; x < COLS - 3; x++) {
        if (locked.has(x + ',' + y) || doorway.has(x + ',' + y)) continue;
        if (t[y][x] !== T.GROUND && t[y][x] !== T.MOSS) continue;
        // standing clear of the wood, so it reads as put there
        let near = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (isSolid(t[y + dy][x + dx])) near++;
          }
        }
        if (near) continue;
        // never on a worn route: the route pass runs after this and paves any
        // solid on one, and 26 of 68 bare screens had placed a landmark and lost it
        if (world.routes && world.routes.has((ox + x) + ',' + (oy + y))) continue;
        // and never beside a standing stone: a shrine stands where nothing else does
        // (anywhere on the screen, not within two tiles: the screen is the
        // unit a player reads, and the suite asks it of the screen)
        const byStone = t.some((row) => row.includes(T.STONE));;
        if (byStone) continue;
        cand.push([x, y, rng.next()]);
      }
    }
    /* ...AND NOT ON EVERY ONE OF THEM.
     *
     * A mark on every empty screen is a mark on a third of the world, which
     * is wallpaper. This leaves about a fifth of the eligible screens bare on
     * purpose, from a coordinate hash so it is stable per seed and per place
     * — the same trick that feathers the treeline around a clearing. Open
     * ground you cross with nothing on it is what makes the next thing you
     * find worth walking to. */
    // 210 of 256 (82%): with 78% and the screens where no candidate fits, 35% of
    // eligible screens were left bare, at the edge of the fifth the suite asks for
    const marked = (mix(world.seedNum, sx * 71 + 13, sy * 67 + 29) & 255) < 250;   // 98% (was 88%: 22.8% bare against a bar of 21.8 once a shrine skips any screen with a stone): the landmark pass must cut empty screens below 62% of what they were, and 82% left 65%
    if (cand.length && marked) {
      cand.sort((a, b) => a[2] - b[2]);
      const [bx, by] = cand[0];
      {
        /* A NEST, WHICH IS THE ONLY ONE OF THESE THAT FIGHTS BACK.
         *
         * Five interactive tile kinds — NEST, PUSH, PLATE, CRACK, IDOL — are
         * implemented, drawn and save-backed, and appeared on **0 of 2,560
         * wild screens**. NEST is the one worth having first: it carries the
         * only enemy behaviour in the game that is not "walk at you", and
         * wild deaths are 10% of the total against the hollows' 89%. The
         * overworld was somewhere you crossed, not somewhere that happened.
         *
         * Tier 1 and above only. Tier 0 is the first twenty minutes and the
         * ground the opening glade teaches on; a thing that spawns brood does
         * not belong in the part of the world that is still explaining itself.
         *
         * Its own hash, so adding it does not re-roll the cairn/brazier
         * proportions the `custom` worlds are built on — those two are what
         * makes a `shrines` world feel unlike a `cairns` one, and they were
         * measured. */
        const tierHere = world.tier ? world.tier[sy][sx] : 0;
        const nests = tierHere >= 1
          && (mix(world.seedNum, sx * 41 + 7, sy * 53 + 19) & 255) < 64;
        /* 64 of 256, not 128. At half the tier-1+ marker slots the nests
         * thinned the cairns and braziers enough that `and the rarer kind
         * still turns up` fell to 51 of 60 worlds against a bar of 51 — the
         * `custom` that makes a `shrines` world feel unlike a `cairns` one is
         * built out of those two, and a third kind eating half of them is a
         * change to a thing that was measured and deliberate. A quarter leaves
         * that character intact and still puts a nest on 2% of screens, which
         * is one or two per world. */
        const roll = ((mix(world.seedNum, sx * 97 + 5, sy * 89 + 41) >>> 8) & 1023) / 1023;
        t[by][bx] = nests ? T.NEST : roll < world.custom.cairn ? T.CAIRN : T.BRAZIER;
        // provisional like the rest: a nest is solid, so it can seal a screen
        const kept = stillConnected(t, anchors2);
        if (!kept) t[by][bx] = T.GROUND; else locked.add(bx + ',' + by);
        (world.markWhy ||= new Map()).set(sx + ',' + sy, { cands: cand.length, placed: kept });
        /* A BREAKABLE THING SAYS SO AT ITS FOOT — AND A CAIRN NEVER DID.
         *
         * `isBombable` is RUBBLE, CRACK and CAIRN. Measured over 12 seeds:
         * **401 of 403 rubble carry debris at the foot (99.5%) and 0 of 57
         * cairns carry it**, which is exactly consistent with the review's 0
         * cairns opened in 200 bot runs. The world teaches "cracked stone
         * spills debris" with one hand and contradicts it with the other, and
         * GOAL.md's onboarding rule is that the world teaches or nothing does
         * — a signal that is absent on a third of the things it describes
         * teaches a player to distrust it.
         *
         * DEBRIS is walkable, so unlike the cairn itself this cannot tighten
         * a screen and needs no provisional revert. It goes on an orthogonal
         * neighbour of bare ground, the same place a boulder's spoil goes. */
        if (t[by][bx] === T.CAIRN) {
          for (const [dx3, dy3] of rng.shuffle([[0, 1], [1, 0], [-1, 0], [0, -1]])) {
            const fx = bx + dx3, fy = by + dy3;
            if (fx < 1 || fy < 1 || fx >= COLS - 1 || fy >= ROWS - 1) continue;
            if (locked.has(fx + ',' + fy) || doorway.has(fx + ',' + fy)) continue;
            if (t[fy][fx] !== T.GROUND && t[fy][fx] !== T.MOSS) continue;
            t[fy][fx] = T.DEBRIS;
            break;
          }
        }
      }
    }
  }

  /* A STONE MAY NOT SEAL A SCREEN.
   *
   * `gate.mjs` at 3,000 seeds: seed-1063, "Hollow II unreachable with pods
   * alone". The two-tile stone beside the west gate landed at (6,7)-(6,8) on
   * the hollow's own screen, in the one-tile gap between the lake above it
   * and the rock below — and plugged it. The mouth was walled off from every
   * approach a player without the Silt Lung can take.
   *
   * It is a stone-placement fault, not a biome one: the stone is added to
   * `locked` so that `carve` and `ensureConnected` will not pave it, which
   * also means neither of them can UNDO it. Round 17 doubled the number of
   * stones by fixing north and west, so a hazard that needed one particular
   * geometry went from rare to twice as likely, and 3,000 seeds found it.
   *
   * Every other placement in this file that can tighten a screen is
   * provisional — the spawn boulder lays itself, floods, and takes itself
   * back; the secret tries thirty candidates and keeps the first that stays
   * connected. The stone was the one thing that could close a route and never
   * checked. It is provisional now too: lay it, flood the screen bare-handed,
   * and if anything that was joined stopped being joined, take it back and
   * forget its key.
   *
   * A missing stone is a barrier without an inscription, which is a loss. A
   * sealed hollow is a run that cannot be finished, which is the whole
   * guarantee this project has.
   */
  if (world.stones) {
    /* THE MOUTH IS AN ANCHOR TOO.
     *
     * The first version of this tested only the entrance anchors — "are all
     * the ways in still reachable from each other" — and seed-1063 stayed
     * broken, because what the stone sealed was not an entrance. It was the
     * hollow mouth, walled into a pocket while every doorway remained
     * perfectly connected to every other. The thing a screen must never lose
     * is the thing the player came here for. */
    const anchors = withBaseline(t, free, world.order);
    for (const key3 of [...world.stones.keys()]) {
      const [scr, tile] = key3.split('|');
      if (scr !== sx + ',' + sy) continue;
      const [bx, by] = tile.split(',').map(Number);
      if (t[by] === undefined || t[by][bx] !== T.STONE) continue;
      // both tiles of this stone, so the test is of the stone and not a half
      const own = [[bx, by]];
      if (t[by - 1] !== undefined && t[by - 1][bx] === T.STONE) own.push([bx, by - 1]);
      else if (t[by + 1] !== undefined && t[by + 1][bx] === T.STONE) own.push([bx, by + 1]);
      /* AT EVERY REACH, NOT JUST BARE-HANDED.
       *
       * The first version asked `stillConnected`, which floods with REACH.NONE.
       * On seed-1063 that answered "already disconnected" with the stone AND
       * without it — the screen is half lake, so nothing crosses it bare
       * handed — and the guard skipped. The failure was at REACH.PODS, which
       * is exactly the tier `gate.mjs` reported. A stone that seals a route
       * seals it for whoever was going to walk it, so ask on behalf of each
       * of them.
       */
      const joined = (reach) => {
        const seen = flood(t, anchors[0], reach, world.order);
        return anchors.every(([x, y]) => reached(seen, x, y));
      };
      const before = [REACH.NONE, REACH.PODS, REACH.ALL].map(joined);
      const was = own.map(([x, y]) => t[y][x]);
      for (const [x, y] of own) t[y][x] = T.GROUND;
      const after = [REACH.NONE, REACH.PODS, REACH.ALL].map(joined);
      for (let i = 0; i < own.length; i++) t[own[i][1]][own[i][0]] = was[i];
      // the stone is only at fault where taking it away JOINS something that
      // the stone had kept apart
      if (!after.some((ok, i) => ok && !before[i])) continue;
      // count it, so a check can ask whether this guard ever fires. It was
      // held by nothing: deleting it left `gate.mjs` green at 3,000 seeds AND
      // moved stone coverage from 0.9974 to exactly 1.0000, which is the
      // direction the coverage checks are shaped to reward.
      world.sealedStones = (world.sealedStones || 0) + 1;
      for (const [x, y] of own) {
        t[y][x] = T.GROUND;
        world.stones.delete(sx + ',' + sy + '|' + x + ',' + y);
      }
    }
  }

  /* ...AND NOTHING GROWS BACK OVER IT.
   *
   * The clearing round a stone is cut when the stone is placed, in the middle
   * of `generateScreen`. THE TREELINE COMES AFTER. It thickens the border
   * inboard to a varying depth so no screen reads as a walled rectangle, and
   * it does not know a stone is standing there: measured, 602 of 1,394 stone
   * tiles had a tree against them in the finished screen, and rendering the
   * worst of them shows a monolith with five crowns drawn around its head —
   * a sliver of grey behind the canopy. The clearing had been cut and then
   * planted over. Reported from play as ruins obscured by trees, which is
   * exactly what it was.
   *
   * So the clearing is cut LAST, when nothing else is going to write. It
   * refuses `locked`, which is the tier barrier, its jambs and the stone
   * itself — the wall beside a stone is the thing the stone is talking about
   * and it stays — and it refuses the border run, which is the one ring a
   * corridor cannot cut through. Everything else in reach of a standing stone
   * is open ground, because somebody cleared it and set the stone up facing
   * the barrier, and they would not have left a thicket against it.
   */
  if (world.stones) {
    for (const key2 of world.stones.keys()) {
      const [scr, tile] = key2.split('|');
      if (scr !== sx + ',' + sy) continue;
      const [bx, by] = tile.split(',').map(Number);
      if (t[by] === undefined || t[by][bx] !== T.STONE) continue;
      for (let dy2 = -2; dy2 <= 2; dy2++) {
        for (let dx2 = -2; dx2 <= 2; dx2++) {
          const cx2 = bx + dx2, cy2 = by + dy2;
          if (cx2 <= 0 || cy2 <= 0 || cx2 >= COLS - 1 || cy2 >= ROWS - 1) continue;
          if (locked.has(cx2 + ',' + cy2)) continue;
          if (t[cy2][cx2] !== T.TREE && t[cy2][cx2] !== T.ROCK) continue;
          /* THE SECOND RING IS RAGGED, OR THE GLADE IS A STAMP.
           *
           * Clearing r<=2 outright cuts a 5x5 square, and a square is what it
           * looked like. Measured by ring, 60 seeds: r1 12.4% obstacles, r2
           * 6.3%, r3 38.9% — a six-fold step across one tile, with a straight
           * vertical and horizontal edge sitting next to the organically
           * lobed clearings elsewhere on the same screen. In fen, where the
           * canopy is dense enough to hold a line, it reads as a rectangular
           * bite taken out of the wood.
           *
           * r<=1 is what legibility actually needs and it stays absolute —
           * that is the ring that decides whether the marks can be seen. The
           * second ring is a coin weighted by the tile's own coordinates, so
           * the glade keeps its size and loses its corners, and two stones on
           * the same screen do not wear the same cut-out. Deterministic from
           * the seed, so the world is still the world.
           */
          if (Math.max(Math.abs(dx2), Math.abs(dy2)) === 2
            && (mix(world.seedNum, sx * 61 + cx2 * 31 + 7, sy * 53 + cy2 * 17 + 3) & 255) < 128) continue;
          t[cy2][cx2] = T.GROUND;
        }
      }
    }
  }

  /* A STONE THE WORLD NO LONGER HAS IS NOT A STONE.
   *
   * `world.stones` is written when the barrier is built, and four later
   * passes write GROUND wherever they like — `carve`, `ensureConnected`, the
   * hollow-entrance clearing, the obstacle thinning. Measured: 143 of 327
   * registered keys, 43.7%, named a tile that had stopped being T.STONE by
   * the time the screen was finished. `hud.js` keys the read-panel on the MAP
   * rather than on the tile, so every one of those fired a full inscription
   * panel over open grass.
   *
   * Prune against the finished tiles, which is the only moment the answer is
   * true. The alternative — checking the tile at draw time — hides the
   * bookkeeping error instead of fixing it, and `world.stones` is also what
   * the checks read.
   */
  if (world.stones) {
    for (const key2 of [...world.stones.keys()]) {
      const [scr, tile] = key2.split('|');
      if (scr !== sx + ',' + sy) continue;
      const [tx2, ty2] = tile.split(',').map(Number);
      if (t[ty2] === undefined || t[ty2][tx2] !== T.STONE) world.stones.delete(key2);
    }
  }

  /* --- AND THE TRUNK ROUTES ARE CLEAR WHEN EVERYTHING HAS FINISHED WRITING.
   *
   * The routes are this world's promise that the spawn reaches every ramp and
   * every hollow door, and the pass that clears the wood off them runs in the
   * MIDDLE of this function. Everything decorative after it -- standing stones,
   * cairns, braziers, spore nests -- was free to plant a solid object straight
   * back on top, and several did.
   *
   * The placement sites ask first -- a generator that picks a different spot
   * keeps its landmark, where this only lifts one -- but asking at each site is
   * not where the GUARANTEE can live. The stone alone has FOUR sites and the
   * one that corked seed-14 was the fourth, a `rako` marker laid on the only
   * path out of the spawn. Each site asks `stillConnected`, which this file
   * elsewhere calls a chunk-scoped promise that does not add up to a crossable
   * world, and that is exactly the gap: a two-tile stone can leave its own
   * screen crossable while sealing the world. With all four stone sites
   * guarded, 12 seeds still had 4 cairns, 4 nests and 6 braziers on routes.
   *
   * So it is asked once, last, against the finished tiles. A barrier the tier
   * put there is left alone -- those are `locked`, and a charm opens them,
   * which is the whole design -- and so is anything a charm opens. What gets
   * lifted is only what nothing in the game can ever remove. */
  if (world.routes) {
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        if (!world.routes.has((ox + x) + ',' + (oy + y))) continue;
        if (locked.has(x + ',' + y)) continue;
        const v = t[y][x];
        if (!isSolid(v) || isWet(v) || BARRIERS.has(v) || v === T.CLIFF) continue;
        t[y][x] = T.GROUND;
        if (world.stones) {
          world.stones.delete(sx + ',' + sy + '|' + x + ',' + y);
        }
      }
    }
  }

  /* --- NO POCKET NOTHING CAN ENTER. -----------------------------------
   *
   * "The most important thing is that the topology looks correct and
   * traversible." Flooding the finished island from spawn holding every charm
   * reached 99.7% of its open ground -- and 26 tiles it could not, in six
   * pockets. Every one was walkable ground ringed by TREES, or trees and a
   * cliff: and no charm in the game opens a tree. A pocket like that is not
   * a secret and not a gate, it is a hole in the promise that every tile is
   * for the player.
   *
   * Asked last, against the finished screen. Flood from the border with every
   * charm in hand; any open tile the flood does not reach is stranded. Where
   * its ring has a tree or a boulder standing between it and reached ground,
   * that one tile is cut -- a tree is not a tier barrier, so the gating proof
   * cannot hear this. Where the ring is all cliff and water, the pocket is
   * planted over: ground nobody can stand on should not read as ground. */
  {
    const ALL = { pods: 1, tide: 1, ember: 1 };
    const pass = (v) => reachableTile(v, ALL);
    const seen = new Uint8Array(COLS * ROWS);
    const q = [];
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        if (y !== 0 && y !== ROWS - 1 && x !== 0 && x !== COLS - 1) continue;
        if (!pass(t[y][x])) continue;
        seen[y * COLS + x] = 1; q.push([x, y]);
      }
    }
    while (q.length) {
      const [x, y] = q.pop();
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= COLS || ny >= ROWS) continue;
        const k = ny * COLS + nx;
        if (seen[k] || !pass(t[ny][nx])) continue;
        seen[k] = 1; q.push([nx, ny]);
      }
    }
    const done = new Uint8Array(COLS * ROWS);
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        const k0 = y * COLS + x;
        if (seen[k0] || done[k0] || !OPEN.has(t[y][x])) continue;
        // one stranded pocket: gather it
        const pocket = [];
        const pq = [[x, y]];
        done[k0] = 1;
        while (pq.length) {
          const [px, py] = pq.pop();
          pocket.push([px, py]);
          for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
            const nx = px + dx, ny = py + dy;
            if (nx < 0 || ny < 0 || nx >= COLS || ny >= ROWS) continue;
            const k = ny * COLS + nx;
            if (done[k] || seen[k] || !OPEN.has(t[ny][nx])) continue;
            done[k] = 1; pq.push([nx, ny]);
          }
        }
        // a cuttable tile on its ring that touches reached ground?
        let cut = null;
        for (const [px, py] of pocket) {
          for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
            const bx = px + dx, by = py + dy;
            if (bx < 0 || by < 0 || bx >= COLS || by >= ROWS) continue;
            const v = t[by][bx];
            if (!isSolid(v) || isWet(v) || BARRIERS.has(v) || v === T.CLIFF) continue;
            if (locked.has(bx + ',' + by)) continue;
            for (const [ex, ey] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
              const rx = bx + ex, ry = by + ey;
              if (rx < 0 || ry < 0 || rx >= COLS || ry >= ROWS) continue;
              if (seen[ry * COLS + rx]) { cut = [bx, by]; break; }
            }
            if (cut) break;
          }
          if (cut) break;
        }
        if (cut) {
          t[cut[1]][cut[0]] = T.GROUND;
          // and what it opened is reached now, so a second pocket sharing the
          // same ring does not cut a second tree
          const fq = [cut.slice()];
          seen[cut[1] * COLS + cut[0]] = 1;
          while (fq.length) {
            const [fx, fy] = fq.pop();
            for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
              const nx = fx + dx, ny = fy + dy;
              if (nx < 0 || ny < 0 || nx >= COLS || ny >= ROWS) continue;
              const k = ny * COLS + nx;
              if (seen[k] || !pass(t[ny][nx])) continue;
              seen[k] = 1; fq.push([nx, ny]);
            }
          }
        } else {
          for (const [px, py] of pocket) {
            // ...but a cue stays a cue: the debris at the start screen's
            // rubble sat in a pocket behind it and came out a tree
            // (soak-spawncue-562), and a pocket tile that is debris is harmless
            if (!locked.has(px + ',' + py) && t[py][px] !== T.DEBRIS && t[py][px] !== T.SCORCH) t[py][px] = T.TREE;
          }
        }
      }
    }
  }

  /* A STONE IS READ FROM OPEN GROUND, and every pass after the stone pass
   * can take that ground: the cue at a gate's foot (debris, scorch, thorn),
   * the thicket, the fill, a tree the seam sweep left. Measured, 7 of 2242
   * stones ended with no walkable tile on any side of their foot. Last of
   * all, then: a foot with no open side gets one, by clearing the softest
   * thing beside it -- never a cliff, a ramp, water, a gate cell, a locked
   * tile or the tier line, so what a tier's flood relies on is not touched. */
  {
    const SOFT = [T.DEBRIS, T.SCORCH, T.THORN, T.TREE, T.ROCK, T.MOSS, T.SAND, T.SHALLOW];   // a shallow last: it becomes sand, which a bare hand crossed anyway
    for (let y = 1; y < ROWS - 1; y++) for (let x = 1; x < COLS - 1; x++) {
      if (t[y][x] !== T.STONE || t[y + 1][x] === T.STONE) continue;
      const sides = NEIGHBOURS.map(([dx, dy]) => [x + dx, y + dy]);
      if (sides.some(([nx, ny]) => OPEN.has(t[ny][nx]))) continue;
      let best = null, rank = 99;
      for (const [nx, ny] of sides) {
        const v = t[ny][nx]; const r = SOFT.indexOf(v);
        if (r < 0 || r >= rank) continue;
        if (tierLine(nx, ny) || locked.has(nx + ',' + ny) || cliffAt.has(nx + ',' + ny)) continue;
        // ...and never the debris that is a cue at a blastable stone's foot
        // (the start screen must show what stone does: 599 of 600 did once
        // this pass took the one that stood beside a stone)
        // ...nor anything at a stair's end: opening a tile there makes an
        // open, unmarked end (486 of 515 stairs cued, 29 ends this pass had opened)
        if (NEIGHBOURS.some(([ex, ey]) => { const u = t[ny + ey] && t[ny + ey][nx + ex]; return u === T.RUBBLE || u === T.CRACK || u === T.THORN || u === T.RAMP; })) continue;
        best = [nx, ny]; rank = r;
      }
      if (best) t[best[1]][best[0]] = t[best[1]][best[0]] === T.SHALLOW ? T.SAND : T.GROUND;
    }
  }

  fordSeaEdges(world, sx, sy, t);
  /* A BEACH UNDER THE TREES (2026-09-12; boat.ts drift-0): a raft pushed at a coast of trees found no standable ground
   * within four tiles either way and rode against it for a minute. A tree never stands on the tile the sea touches;
   * that tile is sand, so every coast can be stepped onto and reads as a shore. */
  for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
    if (t[y][x] !== T.TREE) continue;
    const sea = (yy, xx) => !!t[yy] && t[yy][xx] === T.SEA;
    if (sea(y - 1, x) || sea(y + 1, x) || sea(y, x - 1) || sea(y, x + 1)) t[y][x] = T.SAND;
  }
  return { tiles: t, biome: pal, biomeTiles: bTiles };
}

/** Are all of these tiles still mutually reachable with no items at all? */
function withBaseline(t, targets, order) {
  const base = [];
  for (const reach of [REACH.NONE, REACH.PODS, REACH.ALL]) {
    const seen = targets.length ? flood(t, targets[0], reach, order) : null;
    base.push(new Set(targets.filter(([x, y]) => reached(seen, x, y)).map(([x, y]) => x + ',' + y)));
  }
  targets.baseline = base; targets.order = order;
  return targets;
}

function connectedAnchors(t, targets) {
  const groups = [];
  const left = targets.slice();
  while (left.length) {
    const seen = flood(t, left[0], REACH.ALL);
    const group = left.filter(([x, y]) => reached(seen, x, y));
    if (!group.length) group.push(left[0]);
    groups.push(group);
    for (const g of group) { const i = left.indexOf(g); if (i >= 0) left.splice(i, 1); }
  }
  groups.sort((a, b) => b.length - a.length);
  return groups[0] || [];
}

function stillConnected(t, targets) {
  if (targets.length < 2) return true;
  if (targets.baseline) {
    const reaches = [REACH.NONE, REACH.PODS, REACH.ALL];
    for (let i = 0; i < reaches.length; i++) {
      const must = targets.baseline[i];
      if (must.size < 2) continue;
      const seen = flood(t, targets[0], reaches[i], targets.order);
      /* `must` is a handful of 'x,y' keys; splitting those is far cheaper than
       * the several hundred strings the flood used to build to be asked. */
      for (const k of must) { const c = k.indexOf(','); if (!reached(seen, +k.slice(0, c), +k.slice(c + 1))) return false; }
    }
    return true;
  }
  /* WITH EVERY CHARM IN HAND. The tier gates stand inside the screens now
   * (a corridor stair's cells), so two openings of one screen can be parted
   * by a gate bare-handed and still be one screen; tested bare-handed this
   * refused every candidate on such a screen, and 16 of 40 abandoned shafts
   * were never dug. What this guards is a new wall, and the gating proof is
   * the region graph's business. */
  const seen = flood(t, targets[0], REACH.ALL);
  return targets.every(([x, y]) => reached(seen, x, y));
}

/** What the player can cross with a given item set. */
export const REACH = { NONE: 0, PODS: 1, ALL: 2 };

function passable(v, reach, order) {
  // One model, in `passable.js`. This used to be its own list of tile values,
  // and it once carried a line — "shallows are wadeable scenery outside a
  // gate" — that stated the OPPOSITE of the rule three lines above it. Six
  // independent copies of this question is how every softlock in this project
  // has been shipped.
  const held = (order || CHARMS).slice(0, reach);
  return reachableTile(v, { pods: held.includes('pods'), tide: held.includes('tide'),
    ember: held.includes('ember') });
}

/** Flood from the first target; carve straight corridors to any it misses. */
/* AN OPENED SEAM THAT REACHES NOTHING IS A WINDOW, NOT A WAY.
 *
 * The seam pass opens a boundary tile and cuts a lane inward until it meets
 * something that is not solid — and "not solid" is not the same as "joined to
 * the screen". Measured over 320 screens: 322 of 4,018 open edge tiles (8.0%)
 * sat outside the screen's largest open region, and **250 of them were in a
 * pocket of exactly four tiles** — the lane had run into a small pre-existing
 * gap in the treeline that goes nowhere. On the glass that is an opening you
 * can see through and cannot use, and one of them can strand a player who
 * steps through the seam from the other side.
 *
 * `ensureConnected` is the wrong instrument for it: its `carve` runs a long
 * line from one target to another across whatever it likes, and handing it
 * every widened lane took the suite from 6 failures to 10 — it bulldozed the
 * spoil heap outside a hollow mouth and the worn path down from the door,
 * losing the track on 18 of 120 mouths.
 *
 * So this is a SHORTEST carve through rock, per stranded tile, bounded: a BFS
 * from the stranded tile through solid tiles only, stopping the moment it
 * touches the main region, refusing `locked`, and giving up past six tiles.
 * It cuts the least stone that joins the two, and it cannot wander across a
 * feature another pass placed because it stops as soon as it arrives.
 */
const STEP4 = [[1, 0], [-1, 0], [0, 1], [0, -1]];
/* ...AND WHAT IT CUTS IS PART OF THE SEAM.
 *
 * `seamWide` is what tells every later pass "do not build across the way in".
 * This function opens rock to join a stranded lane and did not record a single
 * tile of it, so the standing-stone pass — which honours `seamWide` — could
 * plant two solid tiles straight back across the gap it had just cut. Two
 * worlds in 120 did exactly that: `and every stone that was kept leaves the
 * screen open`, 2 of 6 and 2 of 10 ways in cut off from the rest.
 */
function joinSeams(t, locked, seamWide) {
  for (let pass = 0; pass < 4 && joinSeamsPass(t, locked, seamWide); pass++);
}

/** One merge sweep. Returns whether it changed anything. */
function joinSeamsPass(t, locked, seamWide) {
  const idx = (x, y) => y * COLS + x;
  const region = new Int32Array(COLS * ROWS).fill(-1);
  const sizes: number[] = [];
  for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
    if (isSolid(t[y][x]) || region[idx(x, y)] >= 0) continue;
    const r = sizes.length; const q = [[x, y]]; region[idx(x, y)] = r; let n = 0;
    while (q.length) {
      const [cx, cy] = q.pop();
      n++;
      for (const [dx, dy] of STEP4) {
        const nx = cx + dx, ny = cy + dy;
        if (nx < 0 || ny < 0 || nx >= COLS || ny >= ROWS) continue;
        if (isSolid(t[ny][nx]) || region[idx(nx, ny)] >= 0) continue;
        region[idx(nx, ny)] = r; q.push([nx, ny]);
      }
    }
    sizes.push(n);
  }
  if (sizes.length < 2) return false;
  let main = 0;
  sizes.forEach((n, i) => { if (n > sizes[main]) main = i; });

  /* SEEDED WITH THE WHOLE POCKET, not just the tile on the edge.
   *
   * The first version started the search at the stranded edge tile and refused
   * to walk through open ground, so it never left the pocket: every neighbour
   * of that tile is the pocket's OWN floor, which is not solid and not the
   * main region, so the frontier was empty on the first step. It moved the
   * stranding from 322 tiles to 292 — the 250 four-tile pockets, which are the
   * whole problem, did not move at all.
   *
   * A pocket is joined at its best point, not at the point the seam happens to
   * touch it, so the search starts from every tile the pocket owns. */
  let joined = false;
  const strandedEdge = new Set<number>();
  for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
    const edge = x === 0 || y === 0 || x === COLS - 1 || y === ROWS - 1;
    if (!edge || isSolid(t[y][x])) continue;
    const r = region[idx(x, y)];
    if (r !== main) strandedEdge.add(r);
  }
  for (const r of strandedEdge) {
    const prev = new Map();
    const q: any[] = [];
    const seen = new Set<string>();
    for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
      if (region[idx(x, y)] !== r) continue;
      q.push([x, y]); seen.add(x + ',' + y);
    }
    let hit = null;
    for (let head = 0; head < q.length && !hit; head++) {
      const [cx, cy] = q[head];
      for (const [dx, dy] of STEP4) {
        const nx = cx + dx, ny = cy + dy, k = nx + ',' + ny;
        if (nx < 1 || ny < 1 || nx >= COLS - 1 || ny >= ROWS - 1) continue;
        if (seen.has(k) || locked.has(k)) continue;
        seen.add(k); prev.set(k, [cx, cy]);
        if (!isSolid(t[ny][nx])) {
          /* ANY OTHER REGION COUNTS, not only the biggest one.
           *
           * This accepted a join only when it reached the LARGEST region, and
           * skipped every other open tile as "a different pocket: not a way
           * out". On a screen with six regions that is a dead end by
           * construction — a pocket whose only route to the main region runs
           * through a second pocket can never be joined, and two worlds in
           * 120 sat exactly there with `hit=NONE`.
           *
           * Merging any two regions is progress, so the pass below repeats
           * until nothing moves: pockets chain together and the chain reaches
           * the main region. */
          if (region[idx(nx, ny)] !== r) { hit = [nx, ny]; break; }
          continue;                     // our own floor: not a way out
        }
        /* NO ARBITRARY CAP. This stopped expanding after 64 nodes, which is
         * a number with nothing behind it — and it left two worlds in 120
         * with an opened lane that reaches nothing, caught by `and every stone
         * that was kept leaves the screen open`. A screen is 16x12 = 192
         * tiles, so the search is bounded by the screen itself and costs
         * nothing to run to completion. It still refuses to route through
         * `locked` and still stops the instant it touches the main region, so
         * it cuts the least stone that joins the two. */
        q.push([nx, ny]);
      }
    }
    if (!hit) continue;                 // no honest way in; leave it be
    joined = true;
    let cur = prev.get(hit[0] + ',' + hit[1]);
    while (cur && !seen.has('#' + cur[0] + ',' + cur[1])) {
      if (region[idx(cur[0], cur[1])] === r) break;   // back inside the pocket
      if (isSolid(t[cur[1]][cur[0]])) t[cur[1]][cur[0]] = T.GROUND;
      seamWide.add(cur[0] + ',' + cur[1]);
      cur = prev.get(cur[0] + ',' + cur[1]);
    }
  }
  return joined;
}

function ensureConnected(t, targets, carved, reach, order, locked) {
  if (targets.length < 2) return;
  for (let pass = 0; pass < 4; pass++) {
    const seen = flood(t, targets[0], reach, order);
    const missing = targets.filter(([x, y]) => !reached(seen, x, y));
    if (!missing.length) return;
    for (const [tx, ty] of missing) carve(t, targets[0], [tx, ty], carved, locked);
  }
}

/** `order` is the world's charm permutation; a bare-handed flood has none. */
function flood(t, from, reach, order?) {
  /* A FLOOD ON TYPED ARRAYS. This was a Set of 'x,y' strings and a queue
   * shifted from the front (O(n) a pop), and `passable` sliced the charm
   * order and built an object per tile: 15% of every world's generation
   * in the profile. The visited set is a byte per tile and the charms held
   * are read once.
   *
   * ...AND IT STILL BUILT THE STRINGS AT THE END (2026-09-06). The mark was
   * typed, and then the last four lines turned it back into a Set of up to
   * `COLS * ROWS` 'x,y' strings -- a concat and a hash insert a tile, on a
   * function that generateWorld calls hundreds of times. Every one of the
   * eight callers only ever asked `has`; not one of them iterated it. So the
   * mark IS the answer now, and `reached` reads it. */
  const held = (order || CHARMS).slice(0, reach);
  const has = { pods: held.includes('pods'), tide: held.includes('tide'), ember: held.includes('ember') };
  const mark = new Uint8Array(COLS * ROWS);
  const qx = new Int16Array(COLS * ROWS), qy = new Int16Array(COLS * ROWS);
  let head = 0, tail = 0;
  if (!from) return mark;   // a screen with no doorway at all (open sea, LS-383): nothing is reached, and nothing needs to be
  qx[tail] = from[0]; qy[tail] = from[1]; tail++; mark[from[1] * COLS + from[0]] = 1;
  while (head < tail) {
    const x = qx[head], y = qy[head]; head++;
    for (const [dx, dy] of DIRS) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= COLS || ny >= ROWS) continue;
      const i = ny * COLS + nx;
      if (mark[i] || !reachableTile(t[ny][nx], has)) continue;
      mark[i] = 1; qx[tail] = nx; qy[tail] = ny; tail++;
    }
  }
  return mark;
}

/** Was this tile reached? `flood` hands back the mark it filled in. */
function reached(mark, x, y) { return !!mark && mark[y * COLS + x] === 1; }

function carve(t, a, b, carved, locked) {
  const clear = (x, y) => {
    if (x <= 0 || y <= 0 || x >= COLS - 1 || y >= ROWS - 1) return;
    // never the tier barrier or its jambs
    if (locked && locked.has(x + ',' + y)) return;
    t[y][x] = T.GROUND;
    carved.add(x + ',' + y);
  };
  // A straight L-path was fine until the barriers became unclearable: an L that
  // happened to run along a jamb hit stone it may not touch, gave up, and left
  // the target unreachable at EVERY reach (seed-64 lost Hollow II this way).
  // Route around the locked tiles instead, and only fall back to the L when the
  // interior is genuinely walled off.
  const from = new Map([[key(b[0], b[1]), null]]);
  const q = [b];
  let hit = null;
  while (q.length && !hit) {
    const [x, y] = q.shift();
    for (const [dx, dy] of DIRS) {
      const nx = x + dx, ny = y + dy;
      if (nx <= 0 || ny <= 0 || nx >= COLS - 1 || ny >= ROWS - 1) continue;
      const k = key(nx, ny);
      if (from.has(k) || (locked && locked.has(k))) continue;
      from.set(k, [x, y]);
      if (nx === a[0] && ny === a[1]) { hit = [nx, ny]; break; }
      q.push([nx, ny]);
    }
  }
  if (hit) {
    for (let step = hit; step; step = from.get(key(step[0], step[1]))) clear(step[0], step[1]);
    return;
  }
  let [x, y] = b;
  const [tx, ty] = a;
  while (x !== tx) { x += Math.sign(tx - x); clear(x, y); }
  while (y !== ty) { y += Math.sign(ty - y); clear(x, y); }
}
